import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

import ThemeProvider from "../theme/TemaMuiProvider";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import { FaChevronDown, FaChevronUp, FaCopy, FaEdit, FaEye, FaPaperclip, FaSave, FaSearch, FaTimes, FaTrash } from "react-icons/fa";

import muiTheme from "../theme/muiTheme";
import { copiarTexto } from "../utils/clipboard";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";

/*
 * O CAR utiliza o mesmo arquivo de estilos da página
 * Matrícula de Imóveis para preservar o padrão visual.
 */
import "./CadastroImoveis.css";
import { limparCpfCnpj, mascararCnpj, mascararCpfCnpj, pareceCnpj, validarCnpj } from "../utils/documento";

/* =========================================================
   CONFIGURAÇÕES
========================================================= */
function criarMatriculaVazia() {
  return {
    numeroMatricula: "",
    dataDocumento: "",
    livro: "",
    folha: "",
    municipioCartorio: "",
  };
}

const FORM_INICIAL = {
  id: "",
  codigoCar: "",

  imovelId: "",
  codigoMatricula: "",
  apelido: "",

  registroCar: "",
  dataCadastro: "",
  nomeImovelRural: "",
  municipio: "",
  uf: "",

  latitude: "",
  longitude: "",
  areaTotalHa: "",
  modulosFiscais: "",
  codigoProtocolo: "",

  tipoPessoa: "",
  cpfCnpj: "",
  nomeRazaoSocial: "",

  matriculas: [criarMatriculaVazia()],

  carEstadual: false,
  observacao: "",
  qtdArquivos: 0,
};

function criarResponsavelCAREstadualVazio() {
  return {
    documento: "",
    tipoPessoa: "",
    nome: "",
  };
}

const FORM_CAR_ESTADUAL_INICIAL = {
  id: null,
  carId: null,
  certificadoInscricaoNumero: "",
  codigoSeguranca: "",
  areaTotalDocumentada: "",
  areaTotalCalculada: "",

  responsaveis: [criarResponsavelCAREstadualVazio()],

  cadastranteDocumento: "",
  cadastranteTipoPessoa: "",
  cadastranteNome: "",
};

const CAMPOS_OBRIGATORIOS = [
  "imovelId",
  "tipoPessoa",
  "cpfCnpj",
  "nomeRazaoSocial",
  "registroCar",
  "dataCadastro",
  "nomeImovelRural",
  "municipio",
  "uf",
  "latitude",
  "longitude",
  "areaTotalHa",
  "modulosFiscais",
  "codigoProtocolo",
];

const CAMPOS_MAIUSCULOS = ["registroCar", "nomeImovelRural", "municipio", "uf", "latitude", "longitude", "codigoProtocolo", "nomeRazaoSocial", "observacao"];

const METRAGEM_DECIMAIS = 4;

/* =========================================================
   FUNÇÕES AUXILIARES
========================================================= */
// Documento limpo (CPF só números; CNPJ numérico ou alfanumérico, letras maiúsculas).
function somenteNumeros(value) {
  return limparCpfCnpj(value);
}

function maskCPF(value) {
  return String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

function maskCNPJ(value) {
  return mascararCnpj(value);
}

function validarCNPJ(value) {
  return validarCnpj(value);
}

function identificarTipoPessoa(value) {
  const digitos = somenteNumeros(value);

  if (!digitos) {
    return "";
  }

  return pareceCnpj(digitos) ? "PJ" : "PF";
}

function maskCpfCnpjInteligente(value) {
  return mascararCpfCnpj(value);
}

function somenteData(value) {
  if (!value) {
    return "";
  }

  return String(value).slice(0, 10);
}

function formatarDataBR(value) {
  if (!value) {
    return "";
  }

  const dataTexto = somenteData(value);
  const partes = dataTexto.split("-");

  if (partes.length !== 3) {
    return dataTexto;
  }

  return `${partes[2]}/${partes[1]}/${partes[0]}`;
}

function removerZerosIniciais(digitos) {
  if (!digitos) {
    return "";
  }

  const resultado = digitos.replace(/^0+(?=\d)/, "");

  return resultado || "0";
}

function formatarMilhares(digitos) {
  return digitos.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/*
 * Máscara aplicada durante a digitação:
 *
 * 1234,5     -> 1.234,5
 * 1234,56789 -> 1.234,5678
 */
function mascaraDecimalDigitacaoBR(value) {
  const texto = String(value ?? "").trim();

  if (!texto) {
    return "";
  }

  /*
   * Remove pontos de milhares e mantém apenas
   * números e uma possível vírgula decimal.
   */
  const limpo = texto.replace(/\./g, "").replace(/[^\d,]/g, "");

  const possuiVirgula = limpo.includes(",");
  const partes = limpo.split(",");

  let inteiro = (partes[0] || "").replace(/\D/g, "");
  const decimal = partes.slice(1).join("").replace(/\D/g, "").slice(0, METRAGEM_DECIMAIS);

  inteiro = removerZerosIniciais(inteiro);

  if (!inteiro) {
    return "";
  }

  const inteiroFormatado = formatarMilhares(inteiro);

  if (!possuiVirgula) {
    return inteiroFormatado;
  }

  return `${inteiroFormatado},${decimal}`;
}

/*
 * Força quatro casas decimais no blur,
 * ao carregar e ao exibir na tabela.
 */
function mascaraDecimal4BR(value) {
  if (value === undefined || value === null || String(value).trim() === "") {
    return "";
  }

  let texto = String(value).trim();

  /*
   * Valores vindos do backend normalmente chegam como:
   * 1234.5678
   */
  if (texto.includes(".") && !texto.includes(",") && /^-?\d+\.\d+$/.test(texto)) {
    const numero = Number(texto);

    if (Number.isFinite(numero)) {
      return numero.toLocaleString("pt-BR", {
        minimumFractionDigits: METRAGEM_DECIMAIS,
        maximumFractionDigits: METRAGEM_DECIMAIS,
      });
    }
  }

  texto = texto.replace(/\./g, "").replace(/[^\d,]/g, "");

  const partes = texto.split(",");

  let inteiro = (partes[0] || "").replace(/\D/g, "");
  let decimal = partes.slice(1).join("").replace(/\D/g, "").slice(0, METRAGEM_DECIMAIS);

  inteiro = removerZerosIniciais(inteiro);

  if (!inteiro) {
    return "";
  }

  decimal = decimal.padEnd(METRAGEM_DECIMAIS, "0");

  return `${formatarMilhares(inteiro)},${decimal}`;
}

function normalizarRegistroParaTela(item) {
  const registro = item || {};

  const matriculasRecebidas = Array.isArray(registro.matriculas) ? registro.matriculas : [];

  const matriculasNormalizadas = matriculasRecebidas.map((matricula) => ({
    id: matricula.id || null,

    numeroMatricula: matricula.numeroMatricula || "",

    dataDocumento: somenteData(matricula.dataDocumento),

    livro: matricula.livro || "",
    folha: matricula.folha || "",

    municipioCartorio: matricula.municipioCartorio || "",
  }));

  return {
    ...registro,

    id: registro.id,

    codigoCar: registro.codigoCar || "",

    imovelId: registro.imovelId || "",

    codigoMatricula: registro.codigoMatricula || "",

    apelido: registro.apelido || "",

    registroCar: registro.registroCar || "",

    dataCadastro: somenteData(registro.dataCadastro),

    nomeImovelRural: registro.nomeImovelRural || "",

    municipio: registro.municipio || "",

    uf: registro.uf || "",

    latitude: registro.latitude || "",

    longitude: registro.longitude || "",

    areaTotalHa: mascaraDecimal4BR(registro.areaTotalHa),

    modulosFiscais: mascaraDecimal4BR(registro.modulosFiscais),

    codigoProtocolo: registro.codigoProtocolo || "",

    tipoPessoa: registro.tipoPessoa || "",

    cpfCnpj: registro.tipoPessoa === "PJ" ? maskCNPJ(registro.cpfCnpj || "") : maskCPF(registro.cpfCnpj || ""),

    nomeRazaoSocial: registro.nomeRazaoSocial || "",

    matriculas: matriculasNormalizadas.length > 0 ? matriculasNormalizadas : [criarMatriculaVazia()],

    carEstadual: Boolean(registro.carEstadual),

    dadosCarEstadual: registro.dadosCarEstadual
      ? {
          certificadoInscricaoNumero: registro.dadosCarEstadual.certificadoInscricaoNumero || "",

          codigoSeguranca: registro.dadosCarEstadual.codigoSeguranca || "",

          areaTotalDocumentada: registro.dadosCarEstadual.areaTotalDocumentada ?? "",

          areaTotalCalculada: registro.dadosCarEstadual.areaTotalCalculada ?? "",

          responsaveis: Array.isArray(registro.dadosCarEstadual.responsaveis) ? registro.dadosCarEstadual.responsaveis : [],

          cadastranteDocumento: registro.dadosCarEstadual.cadastranteDocumento || "",

          cadastranteTipoPessoa: registro.dadosCarEstadual.cadastranteTipoPessoa || "",

          cadastranteNome: registro.dadosCarEstadual.cadastranteNome || "",
        }
      : null,

    observacao: registro.observacao || "",

    qtdArquivos: Number(registro.qtdArquivos || 0),
  };
}

function obterTipoUsuario() {
  const token = localStorage.getItem("token");

  if (!token) {
    return "";
  }

  try {
    const decoded = jwtDecode(token);

    return String(decoded.tipo || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  } catch (error) {
    console.error("Erro ao interpretar token:", error);
    return "";
  }
}

function montarHeaders() {
  const token = localStorage.getItem("token");

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
}

function obterMensagemErro(error, mensagemPadrao) {
  const dadosResposta = error?.response?.data || {};

  const mensagem = dadosResposta.erro || dadosResposta.message || error?.message || mensagemPadrao;

  const campos = Array.isArray(dadosResposta.campos) ? dadosResposta.campos.filter(Boolean).join(", ") : "";

  if (campos) {
    return `${mensagem}\n\nCampos: ${campos}`;
  }

  return mensagem;
}

/* =========================================================
   COMPONENTE
========================================================= */

function CAR() {
  const [form, setForm] = useState(FORM_INICIAL);

  const [abaAtiva, setAbaAtiva] = useState("RECIBO");

  const [formCAREstadual, setFormCAREstadual] = useState(FORM_CAR_ESTADUAL_INICIAL);

  const [carEstadualExiste, setCAREstadualExiste] = useState(false);

  const [carregandoCAREstadual, setCarregandoCAREstadual] = useState(false);

  const [salvandoCAREstadual, setSalvandoCAREstadual] = useState(false);

  const [registros, setRegistros] = useState([]);
  const [filtrados, setFiltrados] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const envioEmAndamentoRef = useRef(false);

  const [busca, setBusca] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [registrosPorPagina, setRegistrosPorPagina] = useState(10);

  const [editandoId, setEditandoId] = useState(null);
  const [modoVisualizacao, setModoVisualizacao] = useState(false);
  const [mostrarPendencias, setMostrarPendencias] = useState(false);

  const [modalJustificativaAberto, setModalJustificativaAberto] = useState(false);

  const [registroPendenteEdicao, setRegistroPendenteEdicao] = useState(null);

  const [justificativaEdicao, setJustificativaEdicao] = useState("");

  const [tipoEdicaoPendente, setTipoEdicaoPendente] = useState(null);

  const [versoesCAR, setVersoesCAR] = useState([]);

  const [carregandoVersoes, setCarregandoVersoes] = useState(false);

  const [modalVersaoAberto, setModalVersaoAberto] = useState(false);

  const [versaoSelecionada, setVersaoSelecionada] = useState(null);

  const [carregandoVersao, setCarregandoVersao] = useState(false);

  const [modalImovelAberto, setModalImovelAberto] = useState(false);
  const [buscaImovel, setBuscaImovel] = useState("");
  const [imoveis, setImoveis] = useState([]);
  const [carregandoImoveis, setCarregandoImoveis] = useState(false);
  const [paginaImoveis, setPaginaImoveis] = useState(0);
  const [totalImoveis, setTotalImoveis] = useState(0);
  const [imoveisPorPagina, setImoveisPorPagina] = useState(10);
  const [consultandoCnpj, setConsultandoCnpj] = useState(false);

  const [nomeBloqueadoReceita, setNomeBloqueadoReceita] = useState(false);

  const [mensagemDocumento, setMensagemDocumento] = useState("");

  const [modalGED, setModalGED] = useState(false);

  const [idSelecionadoGED, setIdSelecionadoGED] = useState(null);

  const [codigoSelecionadoGED, setCodigoSelecionadoGED] = useState("");

  const [arquivosGED, setArquivosGED] = useState([]);

  const [gedPendentes, setGedPendentes] = useState([]);

  const [carregandoGED, setCarregandoGED] = useState(false);

  const [enviandoGED, setEnviandoGED] = useState(false);

  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const salvo = localStorage.getItem("carGridMinimizado");

    return salvo ? salvo === "1" : false;
  });

  const tipoUsuario = obterTipoUsuario();

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);

  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);

  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);

  const podeExcluir = tipoUsuario === "administrador";

  const podeExcluirArquivoGED = ["gestor", "administrador"].includes(tipoUsuario);

  const isReadOnly = modoVisualizacao;

  const totalRegistros = filtrados.length;

  const registrosPaginados = useMemo(() => {
    const inicio = (paginaAtual - 1) * registrosPorPagina;

    return filtrados.slice(inicio, inicio + registrosPorPagina);
  }, [filtrados, paginaAtual, registrosPorPagina]);

  /* =======================================================
     ESTILOS MUI
  ======================================================= */

  const muiCampoSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      color: "var(--ig-t-ffffff)",
      backgroundColor: "transparent",

      "& fieldset": {
        borderColor: "var(--ig-b-555555)",
      },

      "&:hover fieldset": {
        borderColor: "var(--ig-b-777777)",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#2ea8ff",
        borderWidth: 2,
      },

      "&.Mui-error fieldset": {
        borderColor: "#ef4444 !important",
        borderWidth: 2,
      },

      "&.Mui-disabled": {
        backgroundColor: "var(--ig-f-255-255-255-a004)",
      },

      "&.Mui-disabled .MuiInputBase-input": {
        WebkitTextFillColor: "var(--ig-t-d1d5db)",
      },
    },

    "& .MuiInputLabel-root": {
      color: "var(--ig-t-cbd5e1)",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#2ea8ff",
    },

    "& .MuiInputLabel-root.Mui-error": {
      color: "#ef4444",
    },

    "& .MuiInputBase-input": {
      color: "var(--ig-t-ffffff)",
    },

    "& input[type='date']::-webkit-calendar-picker-indicator": {
      filter: "var(--ig-x-11o99ka)",
      cursor: "pointer",
    },
  };

  const menuPropsDark = {
    PaperProps: {
      sx: {
        backgroundColor: "var(--ig-f-111827)",
        color: "var(--ig-t-ffffff)",
      },
    },
  };

  /* =======================================================
     CARREGAMENTO
  ======================================================= */

  const aplicarLista = (lista) => {
    const normalizada = Array.isArray(lista) ? lista.map(normalizarRegistroParaTela) : [];

    setRegistros(normalizada);
    setFiltrados(normalizada);
  };

  const carregarCARs = async () => {
    setCarregando(true);

    try {
      const response = await axios.get("/api/car", {
        headers: montarHeaders(),
      });

      aplicarLista(response.data);
    } catch (error) {
      console.error("Erro ao carregar registros do CAR:", error);

      alert(obterMensagemErro(error, "Erro ao carregar registros do CAR."));
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarCARs();
  }, []);

  useEffect(() => {
    localStorage.setItem("carGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  /*
   * Corrige a página atual caso uma exclusão ou filtro
   * diminua a quantidade total de páginas.
   */
  useEffect(() => {
    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / registrosPorPagina));

    if (paginaAtual > totalPaginas) {
      setPaginaAtual(totalPaginas);
    }
  }, [filtrados.length, paginaAtual, registrosPorPagina]);

  /* =======================================================
     BUSCA DE IMÓVEL
  ======================================================= */

  const carregarImoveis = async (filtro = buscaImovel, pagina = paginaImoveis, porPagina = imoveisPorPagina) => {
    setCarregandoImoveis(true);

    try {
      const response = await axios.get("/api/car/imoveis-selecao", {
        headers: montarHeaders(),
        params: {
          filtro,
          pagina,
          porPagina,
        },
      });

      setImoveis(Array.isArray(response.data?.data) ? response.data.data : []);

      setTotalImoveis(Number(response.data?.total || 0));
    } catch (error) {
      console.error("Erro ao buscar imóveis:", error);

      setImoveis([]);
      setTotalImoveis(0);

      alert(obterMensagemErro(error, "Erro ao buscar imóveis."));
    } finally {
      setCarregandoImoveis(false);
    }
  };

  const abrirModalImovel = () => {
    if (isReadOnly) {
      return;
    }

    setBuscaImovel("");
    setPaginaImoveis(0);
    setModalImovelAberto(true);
  };

  const fecharModalImovel = () => {
    setModalImovelAberto(false);
  };

  const pesquisarImovel = () => {
    setPaginaImoveis(0);

    carregarImoveis(buscaImovel, 0, imoveisPorPagina);
  };

  /*
   * Pesquisa automática no modal de imóveis.
   *
   * Aguarda 400 milissegundos após a última
   * tecla digitada antes de consultar o backend.
   */
  useEffect(() => {
    if (!modalImovelAberto) {
      return undefined;
    }

    const temporizador = window.setTimeout(() => {
      setPaginaImoveis(0);

      carregarImoveis(buscaImovel, 0, imoveisPorPagina);
    }, 400);

    return () => {
      window.clearTimeout(temporizador);
    };
  }, [buscaImovel, modalImovelAberto, imoveisPorPagina]);

  const selecionarImovel = (imovel) => {
    setForm((anterior) => ({
      ...anterior,
      imovelId: imovel.id,
      codigoMatricula: imovel.codigoMatricula || "",
      apelido: imovel.apelido || "",
    }));

    setModalImovelAberto(false);
  };

  /* =======================================================
     FORMULÁRIO
  ======================================================= */

  const estaVazio = (value) => !String(value ?? "").trim();

  const campoComErro = (campo) => {
    if (!mostrarPendencias) {
      return false;
    }

    if (!CAMPOS_OBRIGATORIOS.includes(campo)) {
      return false;
    }

    return estaVazio(form[campo]);
  };

  const consultarCnpj = async (documento) => {
    const docLimpo = somenteNumeros(documento);

    if (docLimpo.length !== 14) {
      return;
    }

    setConsultandoCnpj(true);
    setMensagemDocumento("Consultando dados do CNPJ...");

    try {
      const response = await axios.get("/api/proprietarios/consulta-documento", {
        headers: montarHeaders(),
        params: {
          tipo: "PJ",
          documento: docLimpo,
        },
      });

      const razaoSocial = String(response.data?.nome || "")
        .trim()
        .toUpperCase();

      setForm((anterior) => ({
        ...anterior,
        tipoPessoa: "PJ",
        cpfCnpj: maskCNPJ(docLimpo),
        nomeRazaoSocial: razaoSocial || anterior.nomeRazaoSocial,
      }));

      setNomeBloqueadoReceita(Boolean(razaoSocial));

      setMensagemDocumento(razaoSocial ? "CNPJ localizado. Razão social preenchida automaticamente." : "CNPJ consultado. Informe a razão social.");
    } catch (error) {
      console.error("Erro ao consultar CNPJ:", error);

      setNomeBloqueadoReceita(false);
      setMensagemDocumento(obterMensagemErro(error, "CNPJ não localizado. Informe a razão social manualmente."));
    } finally {
      setConsultandoCnpj(false);
    }
  };

  const handleCpfCnpjChange = async (event) => {
    if (isReadOnly) {
      return;
    }

    const digitos = somenteNumeros(event.target.value);

    const tipoPessoa = identificarTipoPessoa(digitos);

    const documentoFormatado = maskCpfCnpjInteligente(digitos);

    setNomeBloqueadoReceita(false);
    setMensagemDocumento("");

    setForm((anterior) => {
      const mudouTipo = anterior.tipoPessoa && anterior.tipoPessoa !== tipoPessoa;

      return {
        ...anterior,
        cpfCnpj: documentoFormatado,
        tipoPessoa,
        nomeRazaoSocial: mudouTipo ? "" : anterior.nomeRazaoSocial,
      };
    });

    if (tipoPessoa === "PJ" && digitos.length === 14) {
      if (!validarCNPJ(digitos)) {
        setNomeBloqueadoReceita(false);

        setMensagemDocumento("CNPJ inválido. Confira os caracteres informados.");

        return;
      }

      await consultarCnpj(digitos);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    let novoValor = value;

    if (CAMPOS_MAIUSCULOS.includes(name)) {
      novoValor = value.toUpperCase();
    }

    if (name === "uf") {
      novoValor = novoValor.replace(/[^A-Z]/g, "").slice(0, 2);
    }

    setForm((anterior) => ({
      ...anterior,
      [name]: novoValor,
    }));
  };

  const handleDecimalChange = (campo) => (event) => {
    setForm((anterior) => ({
      ...anterior,
      [campo]: mascaraDecimalDigitacaoBR(event.target.value),
    }));
  };

  const handleDecimalBlur = (campo) => (event) => {
    setForm((anterior) => ({
      ...anterior,
      [campo]: mascaraDecimal4BR(event.target.value),
    }));
  };

  const handleMatriculaChange = (index, campo, value) => {
    if (isReadOnly) {
      return;
    }

    const valorTratado = campo === "dataDocumento" ? value : String(value ?? "").toUpperCase();

    setForm((anterior) => ({
      ...anterior,

      matriculas: anterior.matriculas.map((matricula, indice) =>
        indice === index
          ? {
              ...matricula,
              [campo]: valorTratado,
            }
          : matricula,
      ),
    }));
  };

  const adicionarMatricula = () => {
    if (isReadOnly) {
      return;
    }

    setForm((anterior) => ({
      ...anterior,

      matriculas: [...anterior.matriculas, criarMatriculaVazia()],
    }));
  };

  const removerMatricula = (index) => {
    if (isReadOnly) {
      return;
    }

    setForm((anterior) => {
      /*
       * O formulário sempre deve possuir
       * pelo menos uma linha de matrícula.
       */
      if (anterior.matriculas.length <= 1) {
        return {
          ...anterior,

          matriculas: [criarMatriculaVazia()],
        };
      }

      return {
        ...anterior,

        matriculas: anterior.matriculas.filter((_, indice) => indice !== index),
      };
    });
  };

  /* =======================================================
   GED DO CAR
======================================================= */

  const selecionarArquivosGED = (event) => {
    const novosArquivos = Array.from(event.target.files || []);

    if (novosArquivos.length === 0) {
      return;
    }

    setGedPendentes((anteriores) => [...anteriores, ...novosArquivos]);

    /*
     * Permite selecionar novamente o mesmo arquivo.
     */
    event.target.value = "";
  };

  const removerArquivoPendente = (index) => {
    setGedPendentes((anteriores) => anteriores.filter((_, indice) => indice !== index));
  };

  const enviarGED = async (carId, files) => {
    if (!carId || !files || files.length === 0) {
      return;
    }

    const formData = new FormData();

    files.forEach((file) => {
      formData.append("files", file);
    });

    setEnviandoGED(true);

    try {
      await axios.post(`/api/car/${carId}/ged`, formData, {
        headers: montarHeaders(),
      });
    } finally {
      setEnviandoGED(false);
    }
  };

  const carregarArquivosGED = async (carId) => {
    if (!carId) {
      setArquivosGED([]);
      return;
    }

    setCarregandoGED(true);

    try {
      const response = await axios.get(`/api/car/${carId}/ged`, {
        headers: montarHeaders(),
      });

      setArquivosGED(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Erro ao carregar GED do CAR:", error);

      setArquivosGED([]);

      alert(obterMensagemErro(error, "Erro ao carregar os arquivos GED."));
    } finally {
      setCarregandoGED(false);
    }
  };

  const abrirGED = async (carId, codigoCar = "") => {
    if (!carId) {
      return;
    }

    setIdSelecionadoGED(carId);
    setCodigoSelecionadoGED(codigoCar || "");
    setArquivosGED([]);
    setModalGED(true);

    await carregarArquivosGED(carId);
  };

  const fecharGED = () => {
    setModalGED(false);
    setIdSelecionadoGED(null);
    setCodigoSelecionadoGED("");
    setArquivosGED([]);
  };

  const baixarArquivoGED = async (arquivoId, nomeArquivo) => {
    try {
      const response = await axios.get(`/api/car/${idSelecionadoGED}/ged/${arquivoId}`, {
        headers: montarHeaders(),
        responseType: "blob",
      });

      const url = URL.createObjectURL(response.data);

      const link = document.createElement("a");

      link.href = url;
      link.download = nomeArquivo || "arquivo";

      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar GED:", error);

      alert(obterMensagemErro(error, "Erro ao baixar o arquivo GED."));
    }
  };

  const visualizarArquivoGED = async (arquivoId, nomeArquivo) => {
    const extensao = String(nomeArquivo || "")
      .split(".")
      .pop()
      .toLowerCase();

    const visualizaveis = ["pdf", "png", "jpg", "jpeg", "gif", "bmp", "webp", "svg", "txt"];

    /*
     * KMZ e demais formatos devem ser baixados.
     */
    if (!visualizaveis.includes(extensao)) {
      const confirmar = window.confirm(
        `Visualização indisponível.\n\n` +
          `O arquivo "${nomeArquivo}" não possui visualização integrada no ImobGest.\n\n` +
          `Para visualizar este tipo de arquivo, é necessário baixar e abrir em um aplicativo compatível.\n\n` +
          `Deseja baixar agora?`,
      );

      if (confirmar) {
        await baixarArquivoGED(arquivoId, nomeArquivo);
      }

      return;
    }

    try {
      const response = await axios.get(`/api/car/${idSelecionadoGED}/ged/${arquivoId}/visualizar`, {
        headers: montarHeaders(),
        responseType: "blob",
      });

      const url = URL.createObjectURL(response.data);

      window.open(url, "_blank", "noopener,noreferrer");

      window.setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 60000);
    } catch (error) {
      console.error("Erro ao visualizar GED:", error);

      alert(obterMensagemErro(error, "Erro ao visualizar o arquivo GED."));
    }
  };

  const excluirArquivoGED = async (arquivoId) => {
    const confirmar = window.confirm("Deseja realmente excluir este arquivo?");

    if (!confirmar) {
      return;
    }

    try {
      await axios.delete(`/api/car/${idSelecionadoGED}/ged/${arquivoId}`, {
        headers: montarHeaders(),
      });

      alert("Arquivo GED excluído com sucesso!");

      await carregarArquivosGED(idSelecionadoGED);

      await carregarCARs();
    } catch (error) {
      console.error("Erro ao excluir GED:", error);

      alert(obterMensagemErro(error, "Erro ao excluir o arquivo GED."));
    }
  };

  /* =========================================================
   CAR ESTADUAL
========================================================= */

  const preencherCAREstadualAutomatico = () => {
    setFormCAREstadual({
      ...FORM_CAR_ESTADUAL_INICIAL,
      carId: form.id || null,

      certificadoInscricaoNumero: form.codigoProtocolo || "",

      areaTotalDocumentada: "",

      areaTotalCalculada: mascaraDecimal4BR(form.areaTotalHa),
    });

    setCAREstadualExiste(false);
  };

  const carregarCAREstadual = async (carId = form.id, dadosPrincipais = form) => {
    if (!carId) {
      preencherCAREstadualAutomatico();
      return;
    }

    setCarregandoCAREstadual(true);

    try {
      const response = await axios.get(`/api/car/${carId}/estadual`, {
        headers: montarHeaders(),
      });

      const registro = response.data?.registro || {};

      setFormCAREstadual({
        ...FORM_CAR_ESTADUAL_INICIAL,
        ...registro,

        carId: registro.carId || carId,

        certificadoInscricaoNumero: registro.certificadoInscricaoNumero || dadosPrincipais.codigoProtocolo || "",

        areaTotalDocumentada: mascaraDecimal4BR(registro.areaTotalDocumentada ?? ""),

        areaTotalCalculada: mascaraDecimal4BR(dadosPrincipais.areaTotalHa ?? ""),

        responsaveis:
          Array.isArray(registro.responsaveis) && registro.responsaveis.length > 0
            ? registro.responsaveis.map((responsavel) => ({
                documento: maskCpfCnpjInteligente(responsavel.documento || ""),

                tipoPessoa: responsavel.tipoPessoa || "",

                nome: responsavel.nome || "",
              }))
            : registro.responsavelDocumento
              ? [
                  {
                    documento: maskCpfCnpjInteligente(registro.responsavelDocumento),

                    tipoPessoa: registro.responsavelTipoPessoa || "",

                    nome: registro.responsavelNome || "",
                  },
                ]
              : [criarResponsavelCAREstadualVazio()],

        cadastranteDocumento: maskCpfCnpjInteligente(registro.cadastranteDocumento || ""),
      });

      setCAREstadualExiste(Boolean(response.data?.existe));
    } catch (error) {
      console.error("Erro ao carregar CAR Estadual:", error);

      preencherCAREstadualAutomatico();

      alert(obterMensagemErro(error, "Erro ao carregar o CAR Estadual."));
    } finally {
      setCarregandoCAREstadual(false);
    }
  };

  const abrirAbaCAREstadual = async () => {
    if (salvando || salvandoCAREstadual) {
      return;
    }

    if (!Boolean(form.carEstadual)) {
      alert('Selecione "Sim" no campo CAR Estadual antes de abrir esta aba.');

      setAbaAtiva("RECIBO");
      return;
    }

    /*
     * Cadastro novo:
     * não salva nada no backend.
     *
     * Apenas copia os dados já digitados
     * no Recibo para o formulário estadual.
     */
    if (!form.id) {
      setFormCAREstadual((anterior) => ({
        ...anterior,

        carId: null,

        certificadoInscricaoNumero: form.codigoProtocolo || "",

        areaTotalCalculada: mascaraDecimal4BR(form.areaTotalHa),

        responsaveis: Array.isArray(anterior.responsaveis) && anterior.responsaveis.length > 0 ? anterior.responsaveis : [criarResponsavelCAREstadualVazio()],
      }));

      setCAREstadualExiste(false);
      setAbaAtiva("ESTADUAL");

      return;
    }

    /*
     * Cadastro já existente:
     * consulta os dados estaduais no backend.
     */
    setAbaAtiva("ESTADUAL");

    await carregarCAREstadual();
  };
  const handleCAREstadualChange = (event) => {
    const { name, value } = event.target;

    const camposMaiusculos = ["codigoSeguranca", "responsavelNome", "cadastranteNome"];

    setFormCAREstadual((anterior) => ({
      ...anterior,

      [name]: camposMaiusculos.includes(name) ? value.toUpperCase() : value,
    }));
  };

  const buscarRazaoSocialCnpjCAREstadual = async (documento) => {
    const cnpj = somenteNumeros(documento);

    if (cnpj.length !== 14 || !validarCNPJ(cnpj)) {
      return "";
    }

    try {
      const response = await axios.get("/api/proprietarios/consulta-documento", {
        headers: montarHeaders(),
        params: {
          tipo: "PJ",
          documento: cnpj,
        },
      });

      return String(response.data?.nome || "")
        .trim()
        .toUpperCase();
    } catch (error) {
      console.error("Erro ao consultar razão social do CNPJ:", error);

      return "";
    }
  };

  const handleDocumentoCAREstadual = (campoDocumento, campoTipo, campoNome) => async (event) => {
    const documento = somenteNumeros(event.target.value);

    const tipoPessoa = identificarTipoPessoa(documento);

    setFormCAREstadual((anterior) => ({
      ...anterior,

      [campoDocumento]: maskCpfCnpjInteligente(documento),

      [campoTipo]: tipoPessoa,

      [campoNome]: tipoPessoa === "PJ" ? "" : anterior[campoNome],
    }));

    if (tipoPessoa === "PJ" && documento.length === 14 && validarCNPJ(documento)) {
      const razaoSocial = await buscarRazaoSocialCnpjCAREstadual(documento);

      if (razaoSocial) {
        setFormCAREstadual((anterior) => {
          if (somenteNumeros(anterior[campoDocumento]) !== documento) {
            return anterior;
          }

          return {
            ...anterior,
            [campoNome]: razaoSocial,
          };
        });
      }
    }
  };

  const adicionarResponsavelCAREstadual = () => {
    setFormCAREstadual((anterior) => ({
      ...anterior,

      responsaveis: [...anterior.responsaveis, criarResponsavelCAREstadualVazio()],
    }));
  };

  const alterarDocumentoResponsavelCAREstadual = async (indice, valor) => {
    const documento = somenteNumeros(valor);

    const tipoPessoa = identificarTipoPessoa(documento);

    setFormCAREstadual((anterior) => ({
      ...anterior,

      responsaveis: (anterior.responsaveis || []).map((responsavel, indiceAtual) =>
        indiceAtual === indice
          ? {
              ...responsavel,

              documento: maskCpfCnpjInteligente(documento),

              tipoPessoa,

              nome: tipoPessoa === "PJ" ? "" : responsavel.nome,
            }
          : responsavel,
      ),
    }));

    if (tipoPessoa === "PJ" && documento.length === 14 && validarCNPJ(documento)) {
      const razaoSocial = await buscarRazaoSocialCnpjCAREstadual(documento);

      if (razaoSocial) {
        setFormCAREstadual((anterior) => ({
          ...anterior,

          responsaveis: (anterior.responsaveis || []).map((responsavel, indiceAtual) => {
            if (indiceAtual !== indice || somenteNumeros(responsavel.documento) !== documento) {
              return responsavel;
            }

            return {
              ...responsavel,
              nome: razaoSocial,
            };
          }),
        }));
      }
    }
  };

  const alterarNomeResponsavelCAREstadual = (indice, valor) => {
    setFormCAREstadual((anterior) => ({
      ...anterior,

      responsaveis: anterior.responsaveis.map((responsavel, indiceAtual) =>
        indiceAtual === indice
          ? {
              ...responsavel,
              nome: valor.toUpperCase(),
            }
          : responsavel,
      ),
    }));
  };

  const removerResponsavelCAREstadual = (indice) => {
    setFormCAREstadual((anterior) => {
      if (anterior.responsaveis.length === 1) {
        return anterior;
      }

      return {
        ...anterior,

        responsaveis: anterior.responsaveis.filter((_, indiceAtual) => indiceAtual !== indice),
      };
    });
  };

  const validarCAREstadual = () => {
    if (!formCAREstadual.codigoSeguranca.trim()) {
      alert("Informe o Código de Segurança.");
      return false;
    }

    if (formCAREstadual.areaTotalDocumentada === "" || formCAREstadual.areaTotalDocumentada === null) {
      alert("Informe a Área Total Documentada.");
      return false;
    }

    if (formCAREstadual.areaTotalCalculada === "" || formCAREstadual.areaTotalCalculada === null) {
      alert("A Área Total Calculada não foi encontrada nos dados do imóvel rural.");
      return false;
    }

    if (!Array.isArray(formCAREstadual.responsaveis) || formCAREstadual.responsaveis.length === 0) {
      alert("Informe ao menos um Responsável pelo Documento.");
      return false;
    }

    for (let indice = 0; indice < formCAREstadual.responsaveis.length; indice += 1) {
      const responsavel = formCAREstadual.responsaveis[indice];

      const documento = somenteNumeros(responsavel.documento);

      if (![11, 14].includes(documento.length)) {
        alert(`Informe um CPF ou CNPJ válido para o Responsável ${indice + 1}.`);
        return false;
      }

      if (!responsavel.nome.trim()) {
        alert(`Informe o Nome/Razão Social do Responsável ${indice + 1}.`);
        return false;
      }
    }

    const cadastranteDocumento = somenteNumeros(formCAREstadual.cadastranteDocumento);

    if (![11, 14].includes(cadastranteDocumento.length)) {
      alert("Informe um CPF ou CNPJ válido para o Cadastrante.");
      return false;
    }

    if (!formCAREstadual.cadastranteNome.trim()) {
      alert("Informe o Nome/Razão Social do Cadastrante.");
      return false;
    }

    return true;
  };

  const cancelarCAREstadual = () => {
    setAbaAtiva("RECIBO");
  };

  const limparFormulario = () => {
    setForm({
      ...FORM_INICIAL,

      matriculas: [criarMatriculaVazia()],
    });
    setAbaAtiva("RECIBO");

    setFormCAREstadual(FORM_CAR_ESTADUAL_INICIAL);

    setCAREstadualExiste(false);
    setCarregandoCAREstadual(false);
    setSalvandoCAREstadual(false);
    setModoVisualizacao(false);
    setMostrarPendencias(false);
    setModalJustificativaAberto(false);
    setRegistroPendenteEdicao(null);
    setJustificativaEdicao("");
    setVersoesCAR([]);
    setCarregandoVersoes(false);
    setModalVersaoAberto(false);
    setVersaoSelecionada(null);
    setCarregandoVersao(false);
    setNomeBloqueadoReceita(false);
    setMensagemDocumento("");
    setConsultandoCnpj(false);
    setGedPendentes([]);
    setArquivosGED([]);
    setModalGED(false);
    setIdSelecionadoGED(null);
  };

  const carregarNoFormulario = (registro) => {
    const normalizado = normalizarRegistroParaTela(registro);

    setForm({
      id: normalizado.id,

      codigoCar: normalizado.codigoCar,

      imovelId: normalizado.imovelId,

      codigoMatricula: normalizado.codigoMatricula,

      apelido: normalizado.apelido,

      registroCar: normalizado.registroCar,

      dataCadastro: normalizado.dataCadastro,

      nomeImovelRural: normalizado.nomeImovelRural,

      municipio: normalizado.municipio,

      uf: normalizado.uf,

      latitude: normalizado.latitude,

      longitude: normalizado.longitude,

      areaTotalHa: normalizado.areaTotalHa,

      modulosFiscais: normalizado.modulosFiscais,

      codigoProtocolo: normalizado.codigoProtocolo,

      tipoPessoa: normalizado.tipoPessoa,

      cpfCnpj: normalizado.cpfCnpj,

      nomeRazaoSocial: normalizado.nomeRazaoSocial,

      matriculas: normalizado.matriculas.map((matricula) => ({
        ...matricula,
      })),

      carEstadual: normalizado.carEstadual,

      observacao: normalizado.observacao || "",

      qtdArquivos: Number(normalizado.qtdArquivos || 0),
    });

    setNomeBloqueadoReceita(normalizado.tipoPessoa === "PJ" && Boolean(normalizado.nomeRazaoSocial));

    setMensagemDocumento(normalizado.tipoPessoa === "PJ" && normalizado.nomeRazaoSocial ? "Razão social cadastrada para este CNPJ." : "");

    setMostrarPendencias(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const visualizarRegistro = (registro) => {
    if (!registro?.id) {
      alert("Não foi possível localizar o registro selecionado.");
      return;
    }

    /*
     * Sempre inicia a visualização do novo registro
     * pela aba principal do Recibo.
     *
     * Isso impede que a tela permaneça exibindo
     * o Versionamento ou o CAR Estadual do
     * registro visualizado anteriormente.
     */
    setAbaAtiva("RECIBO");

    /*
     * Limpa os estados pertencentes ao
     * Versionamento do registro anterior.
     */
    setVersoesCAR([]);
    setCarregandoVersoes(false);
    setModalVersaoAberto(false);
    setVersaoSelecionada(null);
    setCarregandoVersao(false);

    /*
     * Limpa os estados pertencentes ao
     * CAR Estadual do registro anterior.
     *
     * Os dados estaduais do novo registro
     * serão carregados quando o usuário
     * abrir a aba CAR Estadual.
     */
    setFormCAREstadual(FORM_CAR_ESTADUAL_INICIAL);

    setCAREstadualExiste(false);
    setCarregandoCAREstadual(false);
    setSalvandoCAREstadual(false);

    /*
     * Carrega os dados do novo CAR selecionado
     * no formulário principal.
     */
    carregarNoFormulario(registro);

    setEditandoId(null);
    setModoVisualizacao(true);
  };

  const editarRegistro = (registro) => {
    if (!registro?.id) {
      alert("Não foi possível localizar o registro selecionado.");
      return;
    }

    setRegistroPendenteEdicao(registro);
    setTipoEdicaoPendente("RECIBO");
    setJustificativaEdicao("");
    setModalJustificativaAberto(true);
  };

  const fecharModalJustificativa = () => {
    setModalJustificativaAberto(false);
    setRegistroPendenteEdicao(null);
    setTipoEdicaoPendente(null);
    setJustificativaEdicao("");
  };

  const confirmarEdicao = async () => {
    const justificativa = justificativaEdicao.trim();

    if (!justificativa) {
      alert("Informe a justificativa da alteração.");
      return;
    }

    if (justificativa.length > 1000) {
      alert("A justificativa deve possuir no máximo 1000 caracteres.");
      return;
    }

    if (!registroPendenteEdicao?.id) {
      alert("Não foi possível localizar o registro selecionado.");
      return;
    }

    carregarNoFormulario(registroPendenteEdicao);

    setEditandoId(registroPendenteEdicao.id);

    if (registroPendenteEdicao.carEstadual) {
      await carregarCAREstadual(registroPendenteEdicao.id, registroPendenteEdicao);
    } else {
      setFormCAREstadual(FORM_CAR_ESTADUAL_INICIAL);

      setCAREstadualExiste(false);
    }

    setModoVisualizacao(false);
    setMostrarPendencias(false);
    setModalJustificativaAberto(false);
    setRegistroPendenteEdicao(null);
    setTipoEdicaoPendente(null);
  };

  const carregarVersoesCAR = async (carId = form.id) => {
    if (!carId) {
      setVersoesCAR([]);
      return;
    }

    setCarregandoVersoes(true);

    try {
      const response = await axios.get(`/api/car/${carId}/versoes`, {
        headers: montarHeaders(),
      });

      setVersoesCAR(Array.isArray(response.data?.versoes) ? response.data.versoes : []);
    } catch (error) {
      console.error("Erro ao carregar versões do CAR:", error);

      setVersoesCAR([]);

      alert(obterMensagemErro(error, "Erro ao carregar o versionamento do CAR."));
    } finally {
      setCarregandoVersoes(false);
    }
  };

  const abrirAbaVersionamento = async () => {
    if (!form.id) {
      alert("Salve o CAR antes de consultar o versionamento.");
      return;
    }

    setAbaAtiva("VERSIONAMENTO");
    await carregarVersoesCAR(form.id);
  };

  const visualizarVersaoCAR = async (versaoId) => {
    if (!form.id || !versaoId) {
      return;
    }

    setCarregandoVersao(true);
    setVersaoSelecionada(null);
    setModalVersaoAberto(true);

    try {
      const response = await axios.get(`/api/car/${form.id}/versoes/${versaoId}`, {
        headers: montarHeaders(),
      });

      setVersaoSelecionada(response.data?.versao || null);
    } catch (error) {
      console.error("Erro ao visualizar versão do CAR:", error);

      setModalVersaoAberto(false);

      alert(obterMensagemErro(error, "Erro ao visualizar a versão anterior do CAR."));
    } finally {
      setCarregandoVersao(false);
    }
  };

  const fecharModalVersao = () => {
    setModalVersaoAberto(false);
    setVersaoSelecionada(null);
    setCarregandoVersao(false);
  };

  const formatarDataHoraBR = (valor) => {
    if (!valor) {
      return "";
    }

    const data = new Date(valor);

    if (Number.isNaN(data.getTime())) {
      return String(valor);
    }

    return data.toLocaleString("pt-BR");
  };

  const formatarValorVersao = (campo, valor) => {
    if (valor === null || valor === undefined || valor === "") {
      return "-";
    }

    if (typeof valor === "boolean") {
      return valor ? "SIM" : "NÃO";
    }

    /*
     * Obtém apenas a última parte do caminho.
     *
     * Exemplos:
     * recibo.dataCadastro
     *   -> dataCadastro
     *
     * recibo.matriculas.0.dataDocumento
     *   -> dataDocumento
     *
     * carEstadual.responsaveis.0.documento
     *   -> documento
     */
    const chaveCampo =
      String(campo)
        .split(".")
        .reverse()
        .find((parte) => !/^\d+$/.test(parte)) || String(campo);

    /*
     * Datas armazenadas no snapshot chegam
     * normalmente no padrão ISO:
     *
     * 2016-04-27T00:00:00.000Z
     *
     * No modal devem aparecer como:
     *
     * 27/04/2016
     */
    const camposData = ["dataCadastro", "dataDocumento"];

    if (camposData.includes(chaveCampo)) {
      return formatarDataBR(valor) || "-";
    }

    /*
     * Campos decimais devem seguir o mesmo
     * padrão utilizado no formulário:
     *
     * 2899.0574 -> 2.899,0574
     * 32.24     -> 32,2400
     */
    const camposDecimais = ["areaTotalHa", "modulosFiscais", "areaTotalDocumentada", "areaTotalCalculada"];

    if (camposDecimais.includes(chaveCampo)) {
      return mascaraDecimal4BR(valor) || "-";
    }

    /*
     * O snapshot principal utiliza a chave
     * "documento" para o proprietário e para
     * os responsáveis do CAR Estadual.
     *
     * Também são consideradas as demais
     * nomenclaturas possíveis do cadastro.
     */
    const camposDocumento = ["documento", "cpfCnpj", "responsavelDocumento", "cadastranteDocumento"];

    if (camposDocumento.includes(chaveCampo)) {
      const documento = somenteNumeros(valor);

      if (documento.length === 11) {
        return maskCPF(documento);
      }

      if (documento.length === 14) {
        return maskCNPJ(documento);
      }

      return String(valor);
    }

    return String(valor);
  };

  const NOMES_CAMPOS_VERSAO = {
    codigoCar: "Código do CAR",
    imovelId: "Imóvel",
    codigoMatricula: "Código da matrícula",
    apelido: "Apelido do imóvel",
    registroCar: "Número do registro no CAR",
    dataCadastro: "Data de cadastro",
    nomeImovelRural: "Nome do imóvel rural",
    municipio: "Município",
    uf: "UF",
    latitude: "Latitude",
    longitude: "Longitude",
    areaTotalHa: "Área total (ha)",
    modulosFiscais: "Módulos fiscais",
    codigoProtocolo: "Código do protocolo",
    tipoPessoa: "Tipo de pessoa",
    cpfCnpj: "CPF/CNPJ",
    nomeRazaoSocial: "Nome/Razão social",
    carEstadual: "Possui CAR Estadual",
    observacao: "Observação",

    numeroMatricula: "Número da matrícula",
    dataDocumento: "Data do documento",
    livro: "Livro",
    folha: "Folha",
    municipioCartorio: "Município do cartório",

    certificadoInscricaoNumero: "Número do certificado de inscrição",

    codigoSeguranca: "Código de segurança",

    areaTotalDocumentada: "Área total documentada",

    areaTotalCalculada: "Área total calculada",

    documento: "CPF/CNPJ do responsável",
    nome: "Nome do responsável",

    cadastranteDocumento: "CPF/CNPJ do cadastrante",

    cadastranteTipoPessoa: "Tipo de pessoa do cadastrante",

    cadastranteNome: "Nome do cadastrante",
  };

  const CAMPOS_TECNICOS_VERSAO = ["id", "empresaId", "empresa_id", "carId", "car_id", "ordem", "qtdArquivos"];

  const achatarSnapshot = (valor, prefixo = "", resultado = {}) => {
    if (Array.isArray(valor)) {
      valor.forEach((item, indice) => {
        achatarSnapshot(item, prefixo ? `${prefixo}.${indice}` : String(indice), resultado);
      });

      return resultado;
    }

    if (valor !== null && typeof valor === "object") {
      Object.entries(valor).forEach(([chave, conteudo]) => {
        achatarSnapshot(conteudo, prefixo ? `${prefixo}.${chave}` : chave, resultado);
      });

      return resultado;
    }

    resultado[prefixo] = valor;
    return resultado;
  };

  const obterUltimaChaveCampo = (campo) => {
    const partes = String(campo).split(".");

    return [...partes].reverse().find((parte) => !/^\d+$/.test(parte)) || campo;
  };

  const campoEhTecnico = (campo) => {
    const ultimaChave = obterUltimaChaveCampo(campo);

    return CAMPOS_TECNICOS_VERSAO.includes(ultimaChave);
  };

  const obterNumeroItem = (campo, agrupamento) => {
    const partes = String(campo).split(".");
    const indiceAgrupamento = partes.indexOf(agrupamento);

    if (indiceAgrupamento === -1) {
      return null;
    }

    const possivelIndice = partes[indiceAgrupamento + 1];

    if (!/^\d+$/.test(possivelIndice)) {
      return null;
    }

    return Number(possivelIndice) + 1;
  };

  const obterNomeCampoVersao = (campo) => {
    const ultimaChave = obterUltimaChaveCampo(campo);

    const nomeBase =
      NOMES_CAMPOS_VERSAO[ultimaChave] ||
      ultimaChave
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/_/g, " ")
        .replace(/^./, (letra) => letra.toUpperCase());

    const numeroMatricula = obterNumeroItem(campo, "matriculas");

    if (numeroMatricula) {
      return `Matrícula ${numeroMatricula} — ${nomeBase}`;
    }

    const numeroResponsavel = obterNumeroItem(campo, "responsaveis");

    if (numeroResponsavel) {
      return `Responsável ${numeroResponsavel} — ${nomeBase}`;
    }

    return nomeBase;
  };

  const normalizarCampoAlterado = (item) => (typeof item === "string" ? item : item?.campo || "");

  const verificarCampoAlterado = (campo, camposAlterados) =>
    camposAlterados.some((campoAlterado) => {
      if (!campoAlterado) {
        return false;
      }

      return campo === campoAlterado || campo.startsWith(`${campoAlterado}.`) || campoAlterado.startsWith(`${campo}.`);
    });

  const obterLinhasComparacaoVersao = (versao) => {
    const anteriores = achatarSnapshot(versao?.dadosAnteriores || {});

    const novos = achatarSnapshot(versao?.dadosNovos || {});

    const camposAlterados = Array.isArray(versao?.camposAlterados) ? versao.camposAlterados.map(normalizarCampoAlterado).filter(Boolean) : [];

    return Array.from(new Set([...Object.keys(anteriores), ...Object.keys(novos)]))
      .filter((campo) => !campoEhTecnico(campo))
      .map((campo) => ({
        campo,
        nomeCampo: obterNomeCampoVersao(campo),

        valorAnterior: anteriores[campo],

        valorNovo: novos[campo],

        alterado: verificarCampoAlterado(campo, camposAlterados),
      }));
  };

  const excluirRegistro = async (registro) => {
    const confirmar = window.confirm(`Tem certeza que deseja excluir o registro ${registro.codigoCar}?`);

    if (!confirmar) {
      return;
    }

    try {
      await axios.delete(`/api/car/${registro.id}`, {
        headers: montarHeaders(),
      });

      alert("CAR excluído com sucesso!");

      if (editandoId === registro.id) {
        limparFormulario();
      }

      await carregarCARs();
    } catch (error) {
      console.error("Erro ao excluir CAR:", error);

      alert(obterMensagemErro(error, "Erro ao excluir o CAR."));
    }
  };

  const validarDadosReciboCAR = () => {
    setMostrarPendencias(true);

    const camposFaltando = CAMPOS_OBRIGATORIOS.filter((campo) => estaVazio(form[campo]));

    if (camposFaltando.length > 0) {
      alert("Preencha todos os campos obrigatórios do Recibo.");

      return false;
    }

    if (String(form.uf).length !== 2) {
      alert("Informe uma UF válida.");
      return false;
    }

    const documentoLimpo = somenteNumeros(form.cpfCnpj);

    if (form.tipoPessoa === "PF" && documentoLimpo.length !== 11) {
      alert("O CPF deve possuir 11 números.");
      return false;
    }

    if (form.tipoPessoa === "PJ" && documentoLimpo.length !== 14) {
      alert("O CNPJ deve possuir 14 números.");
      return false;
    }

    if (form.tipoPessoa === "PJ" && !validarCNPJ(documentoLimpo)) {
      alert("O CNPJ informado é inválido. Confira os números.");

      return false;
    }

    if (form.tipoPessoa !== "PF" && form.tipoPessoa !== "PJ") {
      alert("Não foi possível identificar o tipo de pessoa.");

      return false;
    }

    const matriculasInvalidas =
      !Array.isArray(form.matriculas) ||
      form.matriculas.length === 0 ||
      form.matriculas.some(
        (matricula) =>
          !String(matricula.numeroMatricula || "").trim() ||
          !String(matricula.dataDocumento || "").trim() ||
          !String(matricula.livro || "").trim() ||
          !String(matricula.folha || "").trim() ||
          !String(matricula.municipioCartorio || "").trim(),
      );

    if (matriculasInvalidas) {
      alert("Preencha todos os campos das matrículas das propriedades.");

      return false;
    }

    return true;
  };

  const montarPayloadReciboCAR = ({ incluirDadosEstaduais = true } = {}) => {
    const documentoLimpo = somenteNumeros(form.cpfCnpj);

    const matriculas = Array.isArray(form.matriculas) ? form.matriculas : [];

    const responsaveisCAREstadual = Array.isArray(formCAREstadual.responsaveis) ? formCAREstadual.responsaveis : [];

    return {
      imovelId: Number(form.imovelId),

      tipoPessoa: String(form.tipoPessoa || "")
        .trim()
        .toUpperCase(),

      cpfCnpj: documentoLimpo,

      nomeRazaoSocial: String(form.nomeRazaoSocial || "").trim(),

      registroCar: String(form.registroCar || "").trim(),

      dataCadastro: somenteData(form.dataCadastro),

      nomeImovelRural: String(form.nomeImovelRural || "").trim(),

      municipio: String(form.municipio || "").trim(),

      uf: String(form.uf || "")
        .trim()
        .toUpperCase(),

      latitude: String(form.latitude || "").trim(),

      longitude: String(form.longitude || "").trim(),

      areaTotalHa: mascaraDecimal4BR(form.areaTotalHa),

      modulosFiscais: mascaraDecimal4BR(form.modulosFiscais),

      codigoProtocolo: String(form.codigoProtocolo || "").trim(),

      matriculas: matriculas.map((matricula) => ({
        numeroMatricula: String(matricula.numeroMatricula || "").trim(),

        dataDocumento: somenteData(matricula.dataDocumento),

        livro: String(matricula.livro || "").trim(),

        folha: String(matricula.folha || "").trim(),

        municipioCartorio: String(matricula.municipioCartorio || "").trim(),
      })),

      carEstadual: Boolean(form.carEstadual),

      observacao: String(form.observacao || "").trim(),

      dadosCarEstadual:
        incluirDadosEstaduais && Boolean(form.carEstadual)
          ? {
              codigoSeguranca: String(formCAREstadual.codigoSeguranca || "").trim(),

              areaTotalDocumentada: mascaraDecimal4BR(formCAREstadual.areaTotalDocumentada),

              areaTotalCalculada: mascaraDecimal4BR(formCAREstadual.areaTotalCalculada),

              responsaveis: responsaveisCAREstadual.map((responsavel) => ({
                documento: somenteNumeros(responsavel.documento),

                tipoPessoa: String(responsavel.tipoPessoa || "")
                  .trim()
                  .toUpperCase(),

                nome: String(responsavel.nome || "").trim(),
              })),

              cadastranteDocumento: somenteNumeros(formCAREstadual.cadastranteDocumento),

              cadastranteTipoPessoa: String(formCAREstadual.cadastranteTipoPessoa || "")
                .trim()
                .toUpperCase(),

              cadastranteNome: String(formCAREstadual.cadastranteNome || "").trim(),
            }
          : null,
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (modoVisualizacao || salvando || envioEmAndamentoRef.current) {
      return;
    }

    setMostrarPendencias(true);

    const camposFaltando = CAMPOS_OBRIGATORIOS.filter((campo) => estaVazio(form[campo]));

    if (camposFaltando.length > 0) {
      alert("Preencha todos os campos obrigatórios.");
      return;
    }

    if (String(form.uf).length !== 2) {
      alert("Informe uma UF válida.");
      return;
    }

    const documentoLimpo = somenteNumeros(form.cpfCnpj);

    if (form.tipoPessoa === "PF" && documentoLimpo.length !== 11) {
      alert("O CPF deve possuir 11 números.");
      return;
    }

    if (form.tipoPessoa === "PJ" && documentoLimpo.length !== 14) {
      alert("O CNPJ deve possuir 14 números.");
      return;
    }

    if (form.tipoPessoa === "PJ" && !validarCNPJ(documentoLimpo)) {
      alert("O CNPJ informado é inválido. Confira os números.");
      return;
    }

    if (form.tipoPessoa !== "PF" && form.tipoPessoa !== "PJ") {
      alert("Não foi possível identificar o tipo de pessoa.");
      return;
    }
    const matriculasInvalidas =
      !Array.isArray(form.matriculas) ||
      form.matriculas.length === 0 ||
      form.matriculas.some(
        (matricula) =>
          !String(matricula.numeroMatricula || "").trim() ||
          !String(matricula.dataDocumento || "").trim() ||
          !String(matricula.livro || "").trim() ||
          !String(matricula.folha || "").trim() ||
          !String(matricula.municipioCartorio || "").trim(),
      );

    if (matriculasInvalidas) {
      alert("Preencha todos os campos das matrículas das propriedades.");
      return;
    }

    if (Boolean(form.carEstadual) && !validarCAREstadual()) {
      setAbaAtiva("ESTADUAL");
      return;
    }

    const payload = montarPayloadReciboCAR({
      incluirDadosEstaduais: true,
    });

    if (envioEmAndamentoRef.current) {
      return;
    }

    envioEmAndamentoRef.current = true;
    setSalvando(true);

    try {
      let carIdSalvo = editandoId;
      let mensagemSucesso = "";

      if (editandoId) {
        const justificativa = justificativaEdicao.trim();

        if (!justificativa) {
          alert("A justificativa da alteração não foi informada.");
          setSalvando(false);
          return;
        }

        await axios.put(
          `/api/car/${editandoId}`,
          {
            ...payload,
            justificativa,
          },
          {
            headers: montarHeaders(),
          },
        );

        mensagemSucesso = "CAR atualizado com sucesso!";
      } else {
        const response = await axios.post("/api/car", payload, {
          headers: montarHeaders(),
        });

        carIdSalvo = response.data?.id;

        mensagemSucesso = `CAR cadastrado com sucesso! Código: ${response.data?.codigoGerado || response.data?.codigoCar || ""}`;
      }

      if (carIdSalvo && gedPendentes.length > 0) {
        await enviarGED(carIdSalvo, gedPendentes);

        mensagemSucesso += `\n${gedPendentes.length} arquivo(s) GED enviado(s).`;
      }

      alert(mensagemSucesso);

      limparFormulario();
      await carregarCARs();
    } catch (error) {
      console.error("Erro ao salvar CAR:", error);

      console.error("Status recebido:", error?.response?.status);

      console.error("Resposta do backend:", error?.response?.data);

      console.error("Payload enviado ao backend:", payload);

      alert(obterMensagemErro(error, "Erro ao salvar o CAR."));
    } finally {
      envioEmAndamentoRef.current = false;
      setSalvando(false);
    }
  };

  /* =======================================================
     FILTRO, CÓPIA E CSV
  ======================================================= */

  const handleBusca = (event) => {
    const valor = event.target.value;

    setBusca(valor);
    setPaginaAtual(1);

    if (!valor.trim()) {
      setFiltrados(registros);
      return;
    }

    const termos = valor
      .toLowerCase()
      .split(/[;,]/)
      .map((termo) => termo.trim())
      .filter(Boolean);

    const resultado = registros.filter((registro) => {
      const texto = Object.values(registro)
        .filter((valorItem) => valorItem !== null && valorItem !== undefined)
        .join(" ")
        .toLowerCase();

      return termos.every((termo) => texto.includes(termo));
    });

    setFiltrados(resultado);
  };

  const limparFiltro = () => {
    setBusca("");
    setFiltrados(registros);
    setPaginaAtual(1);
  };

  const formatarResponsaveisCAREstadual = (responsaveis) => {
    if (!Array.isArray(responsaveis) || responsaveis.length === 0) {
      return "";
    }

    return responsaveis
      .map((responsavel, indice) => {
        const nome = responsavel?.nome || "";

        const tipoPessoa = responsavel?.tipoPessoa || "";

        const documento = responsavel?.documento || "";

        return [`Responsável ${indice + 1}:`, nome, tipoPessoa ? `| ${tipoPessoa}` : "", documento ? `| ${documento}` : ""].filter(Boolean).join(" ");
      })
      .join(" / ");
  };

  const cabecalhosExportacao = [
    "Código CAR",
    "Código Matrícula",
    "Apelido",
    "Registro no CAR",
    "Data de Cadastro",
    "Nome do Imóvel Rural",
    "CAR Estadual",

    "Número do Certificado de Inscrição Estadual",
    "Código de Segurança Estadual",
    "Área Total Documentada Estadual",
    "Área Total Calculada Estadual",
    "Responsáveis pelo Documento Estadual",
    "Documento do Cadastrante Estadual",
    "Tipo de Pessoa do Cadastrante Estadual",
    "Nome/Razão Social do Cadastrante Estadual",

    "Município",
    "UF",
    "Latitude",
    "Longitude",
    "Área Total (ha)",
    "Módulos Fiscais",
    "Código do Protocolo",
  ];

  const linhaExportacao = (registro) => {
    const dadosEstaduais = registro.dadosCarEstadual || {};

    return [
      registro.codigoCar || "",
      registro.codigoMatricula || "",
      registro.apelido || "",
      registro.registroCar || "",

      formatarDataBR(registro.dataCadastro),

      registro.nomeImovelRural || "",

      registro.carEstadual ? "SIM" : "NÃO",

      dadosEstaduais.certificadoInscricaoNumero || "",

      dadosEstaduais.codigoSeguranca || "",

      dadosEstaduais.areaTotalDocumentada === "" || dadosEstaduais.areaTotalDocumentada === null || dadosEstaduais.areaTotalDocumentada === undefined
        ? ""
        : mascaraDecimal4BR(dadosEstaduais.areaTotalDocumentada),

      dadosEstaduais.areaTotalCalculada === "" || dadosEstaduais.areaTotalCalculada === null || dadosEstaduais.areaTotalCalculada === undefined
        ? ""
        : mascaraDecimal4BR(dadosEstaduais.areaTotalCalculada),

      formatarResponsaveisCAREstadual(dadosEstaduais.responsaveis),

      dadosEstaduais.cadastranteDocumento || "",

      dadosEstaduais.cadastranteTipoPessoa || "",

      dadosEstaduais.cadastranteNome || "",

      registro.municipio || "",
      registro.uf || "",
      registro.latitude || "",
      registro.longitude || "",

      mascaraDecimal4BR(registro.areaTotalHa),

      mascaraDecimal4BR(registro.modulosFiscais),

      registro.codigoProtocolo || "",
    ];
  };

  const copiarTabela = async () => {
    const linhas = filtrados.map((registro) => linhaExportacao(registro).join("\t"));

    const texto = [cabecalhosExportacao.join("\t"), ...linhas].join("\n");

    const sucesso = await copiarTexto(texto);

    alert(sucesso ? "Registros copiados para a área de transferência!" : "Não foi possível copiar os registros.");
  };

  const escaparCSV = (value) => {
    const texto = String(value ?? "").replace(/"/g, '""');

    return `"${texto}"`;
  };

  const exportarCSV = () => {
    const BOM = "\uFEFF";

    const linhas = filtrados.map((registro) => linhaExportacao(registro).map(escaparCSV).join(";"));

    const conteudo = [cabecalhosExportacao.map(escaparCSV).join(";"), ...linhas].join("\r\n");

    const blob = new Blob([BOM + conteudo], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "car.csv";

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);
  };

  /* =======================================================
     RENDERIZAÇÃO
  ======================================================= */

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="local-container">
        <h2>Cadastro Ambiental Rural - CAR</h2>

        {/* BARRA DE BUSCA */}
        <Box
          className="bloco-linha"
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              height: 52,
              width: "100%",
              border: "1px solid var(--ig-b-555555)",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              backgroundColor: "transparent",
              boxSizing: "border-box",
              px: 2,
            }}
          >
            <input
              type="text"
              placeholder="Filtrar registros do CAR"
              value={busca}
              onChange={handleBusca}
              style={{
                flex: 1,
                height: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                color: "var(--ig-t-ffffff)",
                fontSize: "1rem",
              }}
            />

            <SearchIcon
              sx={{
                color: "var(--ig-t-9ca3af)",
                fontSize: 20,
                flexShrink: 0,
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={limparFiltro}
              sx={{
                height: 52,
                color: "#2196f3",
                borderColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",

                "&:hover": {
                  borderColor: "#42a5f5",
                  backgroundColor: "rgba(33, 150, 243, 0.08)",
                },
              }}
            >
              Limpar Filtro
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={copiarTabela}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",

                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              <FaCopy style={{ marginRight: 6 }} />
              Copiar
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={exportarCSV}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",

                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              CSV
            </Button>
          </Box>
        </Box>

        {/* MINIMIZAR/EXPANDIR GRID */}
        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button type="button" className="btn-grid-toggle small danger" onClick={() => setGridMinimizado(true)}>
              <FaChevronDown />
              Minimizar
            </button>
          ) : (
            <button type="button" className="btn-grid-toggle small outline" onClick={() => setGridMinimizado(false)}>
              <FaChevronUp />
              Expandir
            </button>
          )}
        </div>

        {/* GRID */}
        {!gridMinimizado && (
          <div className="bloco-lista">
            {carregando ? (
              <p>Carregando...</p>
            ) : filtrados.length === 0 ? (
              <p>Não há registros para exibir</p>
            ) : (
              <>
                <table
                  className="tabela-matriculas"
                  style={{
                    width: "100%",
                    tableLayout: "fixed",
                  }}
                >
                  <colgroup>
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "18%" }} />
                  </colgroup>

                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Imóvel</th>
                      <th>Apelido</th>
                      <th>Nome do Imóvel Rural</th>
                      <th>CAR Estadual</th>
                      <th>Município/UF</th>
                      <th>Ações</th>
                    </tr>
                  </thead>

                  <tbody>
                    {registrosPaginados.map((registro) => (
                      <tr key={registro.id}>
                        <td>{registro.codigoCar}</td>

                        <td>{registro.codigoMatricula}</td>

                        <td>{registro.apelido}</td>

                        <td>{registro.nomeImovelRural}</td>

                        <td>{registro.carEstadual ? "SIM" : "NÃO"}</td>

                        <td>{[registro.municipio, registro.uf].filter(Boolean).join("/")}</td>

                        <td className="col-acoes">
                          <button
                            type="button"
                            className={`btn-ged-lista ${Number(registro.qtdArquivos) > 0 ? "btn-ged-ok" : ""}`}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();

                              abrirGED(registro.id, registro.codigoCar);
                            }}
                            aria-label={`GED ${registro.codigoCar}`}
                          >
                            GED
                            {Number(registro.qtdArquivos) > 0 && <span className="ged-badge">{registro.qtdArquivos}</span>}
                          </button>

                          {podeVisualizar && (
                            <button
                              type="button"
                              className="btn-editar"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();

                                visualizarRegistro(registro);
                              }}
                              aria-label={`Visualizar ${registro.codigoCar}`}
                            >
                              <FaEye />
                              Visualizar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                    pt: 1,
                    overflow: "hidden",
                  }}
                >
                  <TablePagination
                    component="div"
                    count={totalRegistros}
                    page={paginaAtual - 1}
                    onPageChange={(_, novaPagina) => setPaginaAtual(novaPagina + 1)}
                    rowsPerPage={registrosPorPagina}
                    onRowsPerPageChange={(event) => {
                      setRegistrosPorPagina(Number(event.target.value));
                      setPaginaAtual(1);
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage="Registros por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                    SelectProps={{
                      MenuProps: menuPropsDark,
                    }}
                    sx={{
                      color: "var(--ig-t-ffffff)",
                      fontSize: "0.8rem",

                      "& .MuiTablePagination-selectIcon": {
                        color: "var(--ig-t-ffffff)",
                      },

                      "& .MuiTablePagination-actions button": {
                        color: "var(--ig-t-ffffff)",
                      },
                    }}
                  />
                </Box>
              </>
            )}
          </div>
        )}

        {/* GED — PADRÃO MATRÍCULA DE IMÓVEIS */}
        {modalGED && (
          <div className="modal-ged-overlay">
            <div className="modal-ged">
              <h3>Arquivos GED - CAR #{codigoSelecionadoGED || idSelecionadoGED}</h3>

              {carregandoGED ? (
                <p>Carregando arquivos...</p>
              ) : arquivosGED.length === 0 ? (
                <p>Nenhum arquivo cadastrado para este CAR.</p>
              ) : (
                <ul
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {arquivosGED.map((arquivo) => {
                    const arquivoId = arquivo.id || arquivo.nome;

                    return (
                      <li
                        key={arquivoId}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                          }}
                        >
                          {arquivo.nome}
                        </span>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <button type="button" className="btn-visualizar-ged" onClick={() => visualizarArquivoGED(arquivoId, arquivo.nome)}>
                            <FaEye />
                            Visualizar
                          </button>

                          <button type="button" className="btn-baixar-ged" onClick={() => baixarArquivoGED(arquivoId, arquivo.nome)}>
                            Baixar
                          </button>

                          {podeExcluirArquivoGED && (
                            <button type="button" className="btn-excluir-ged" onClick={() => excluirArquivoGED(arquivoId)}>
                              <FaTrash />
                              Excluir
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              <button type="button" className="btn-fechar-ged" onClick={fecharGED}>
                Fechar
              </button>
            </div>
          </div>
        )}

        {/* FORMULÁRIO */}
        {podeCadastrar || (podeVisualizar && modoVisualizacao) || (podeEditar && editandoId) ? (
          <div className="bloco-formulario">
            <form
              className="form-grid"
              onSubmit={(event) => {
                if (modoVisualizacao) {
                  event.preventDefault();
                  event.stopPropagation();
                  return;
                }

                handleSubmit(event);
              }}
            >
              {/* LINHA 1 - SELEÇÃO DO IMÓVEL */}
              <Box
                sx={{
                  width: {
                    xs: "100%",
                    md: "420px",
                  },
                  maxWidth: "100%",
                }}
              >
                <TextField
                  label="Apelido*"
                  name="apelido"
                  value={form.apelido}
                  placeholder="Clique para buscar um imóvel"
                  error={campoComErro("imovelId")}
                  fullWidth
                  size="small"
                  sx={{
                    ...muiCampoSx,

                    "& .MuiOutlinedInput-root": {
                      ...muiCampoSx["& .MuiOutlinedInput-root"],

                      cursor: isReadOnly ? "default" : "pointer",
                    },

                    "& .MuiInputBase-input": {
                      color: "var(--ig-t-ffffff)",

                      cursor: isReadOnly ? "default" : "pointer",
                    },
                  }}
                  onClick={() => {
                    if (!isReadOnly) {
                      abrirModalImovel();
                    }
                  }}
                  InputProps={{
                    readOnly: true,

                    endAdornment: (
                      <Tooltip title={isReadOnly ? "Campo somente para visualização" : "Buscar imóvel"}>
                        <span>
                          <IconButton
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();

                              if (!isReadOnly) {
                                abrirModalImovel();
                              }
                            }}
                            disabled={isReadOnly}
                            sx={{
                              color: isReadOnly ? "#64748b" : "var(--ig-t-ffffff)",
                            }}
                          >
                            <FaSearch />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ),
                  }}
                />
              </Box>

              {/* =====================================================
    ABAS DO FORMULÁRIO CAR
===================================================== */}
              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "6px",
                  borderBottom: "1px solid var(--ig-b-334155)",
                }}
              >
                <Button
                  type="button"
                  onClick={() => setAbaAtiva("RECIBO")}
                  sx={{
                    width: "360px",
                    minWidth: "460px",
                    height: "40px",
                    flexShrink: 0,
                    px: 2,

                    color: abaAtiva === "RECIBO" ? "#ffffff" : "#a3a3a3",

                    backgroundColor: abaAtiva === "RECIBO" ? "#0d6efd" : "var(--ig-f-182334)",

                    borderRadius: "4px 4px 0 0",
                    fontFamily: "Arial, sans-serif",
                    fontWeight: 700,
                    fontSize: "20px",
                    whiteSpace: "nowrap",
                    textTransform: "none",

                    boxShadow: abaAtiva === "RECIBO" ? "0 4px 8px rgba(13, 110, 253, 0.28)" : "none",

                    "&:hover": {
                      backgroundColor: abaAtiva === "RECIBO" ? "#0b5ed7" : "var(--ig-f-202d40)",
                    },
                  }}
                >
                  Recibo de Inscrição do Imóvel Rural no CAR
                </Button>

                {Boolean(form.carEstadual) && (
                  <Button
                    type="button"
                    onClick={abrirAbaCAREstadual}
                    disabled={salvando || salvandoCAREstadual}
                    sx={{
                      minWidth: "330px",
                      height: "40px",
                      px: 2,
                      color: abaAtiva === "ESTADUAL" ? "#ffffff" : "#a3a3a3",
                      backgroundColor: abaAtiva === "ESTADUAL" ? "#0d6efd" : "var(--ig-f-182334)",
                      borderRadius: "4px 4px 0 0",
                      fontFamily: "Arial, sans-serif",
                      fontWeight: 700,
                      fontSize: "20px",
                      textTransform: "none",
                      boxShadow: abaAtiva === "ESTADUAL" ? "0 4px 8px rgba(13, 110, 253, 0.28)" : "none",

                      "&:hover": {
                        backgroundColor: abaAtiva === "ESTADUAL" ? "#0b5ed7" : "var(--ig-f-202d40)",
                      },
                    }}
                  >
                    CAR Estadual
                  </Button>
                )}
                {Boolean(form.id) && (
                  <Button
                    type="button"
                    onClick={abrirAbaVersionamento}
                    sx={{
                      minWidth: "250px",
                      height: "40px",
                      px: 2,
                      color: abaAtiva === "VERSIONAMENTO" ? "#ffffff" : "#a3a3a3",
                      backgroundColor: abaAtiva === "VERSIONAMENTO" ? "#0d6efd" : "var(--ig-f-182334)",
                      borderRadius: "4px 4px 0 0",
                      fontFamily: "Arial, sans-serif",
                      fontWeight: 700,
                      fontSize: "20px",
                      whiteSpace: "nowrap",
                      textTransform: "none",
                      boxShadow: abaAtiva === "VERSIONAMENTO" ? "0 4px 8px rgba(13, 110, 253, 0.28)" : "none",

                      "&:hover": {
                        backgroundColor: abaAtiva === "VERSIONAMENTO" ? "#0b5ed7" : "var(--ig-f-202d40)",
                      },
                    }}
                  >
                    Versionamento
                  </Button>
                )}
              </Box>

              {abaAtiva === "RECIBO" && (
                <>
                  {/* REGISTRO E DATA */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "minmax(0, 1fr) 220px",
                      },
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    <TextField
                      label="Registro no CAR*"
                      name="registroCar"
                      value={form.registroCar}
                      onChange={handleChange}
                      disabled={isReadOnly}
                      error={campoComErro("registroCar")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="Data de Cadastro*"
                      type="date"
                      name="dataCadastro"
                      value={form.dataCadastro}
                      onChange={handleChange}
                      disabled={isReadOnly}
                      error={campoComErro("dataCadastro")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                    />
                  </Box>

                  {/* NOME DO IMÓVEL, MUNICÍPIO E UF */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "minmax(0, 1fr) 260px 100px",
                      },
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    <TextField
                      label="Nome do Imóvel Rural*"
                      name="nomeImovelRural"
                      value={form.nomeImovelRural}
                      onChange={handleChange}
                      disabled={isReadOnly}
                      error={campoComErro("nomeImovelRural")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="Município*"
                      name="municipio"
                      value={form.municipio}
                      onChange={handleChange}
                      disabled={isReadOnly}
                      error={campoComErro("municipio")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="UF*"
                      name="uf"
                      value={form.uf}
                      onChange={handleChange}
                      disabled={isReadOnly}
                      error={campoComErro("uf")}
                      inputProps={{
                        maxLength: 2,
                      }}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />
                  </Box>

                  {/* COORDENADAS */}
                  <Box
                    sx={{
                      mt: 1,
                      pb: 1,
                      borderBottom: "1px solid var(--ig-b-255-255-255-a018)",
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        color: "var(--ig-t-ffffff)",
                        fontSize: "1rem",
                        fontWeight: 700,
                      }}
                    >
                      Coordenadas Geográficas do Centroide do Imóvel Rural
                    </h3>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: `
        minmax(0, 1fr)
        minmax(0, 1fr)
        330px
        180px
      `,
                      },
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    <TextField
                      label="Latitude*"
                      name="latitude"
                      value={form.latitude}
                      onChange={handleChange}
                      disabled={isReadOnly}
                      error={campoComErro("latitude")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="Longitude*"
                      name="longitude"
                      value={form.longitude}
                      onChange={handleChange}
                      disabled={isReadOnly}
                      error={campoComErro("longitude")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="Área Total (ha) do Imóvel Rural*"
                      name="areaTotalHa"
                      value={form.areaTotalHa}
                      onChange={handleDecimalChange("areaTotalHa")}
                      onBlur={handleDecimalBlur("areaTotalHa")}
                      disabled={isReadOnly}
                      error={campoComErro("areaTotalHa")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="Módulos Fiscais*"
                      name="modulosFiscais"
                      value={form.modulosFiscais}
                      onChange={handleDecimalChange("modulosFiscais")}
                      onBlur={handleDecimalBlur("modulosFiscais")}
                      disabled={isReadOnly}
                      error={campoComErro("modulosFiscais")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />
                  </Box>

                  {/* CÓDIGO DO PROTOCOLO */}
                  <Box
                    sx={{
                      width: {
                        xs: "100%",
                        md: "420px",
                      },
                      maxWidth: "100%",
                    }}
                  >
                    <TextField
                      label="Código do Protocolo*"
                      name="codigoProtocolo"
                      value={form.codigoProtocolo}
                      onChange={handleChange}
                      disabled={isReadOnly}
                      error={campoComErro("codigoProtocolo")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />
                  </Box>

                  {/* =====================================================
    IDENTIFICAÇÃO DO PROPRIETÁRIO/POSSUIDOR
===================================================== */}
                  <Box
                    sx={{
                      mt: 1,
                      pb: 1,
                      borderBottom: "1px solid var(--ig-b-255-255-255-a018)",
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        color: "var(--ig-t-ffffff)",
                        fontSize: "1.05rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                      }}
                    >
                      Identificação do Proprietário/Possuidor
                    </h3>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "260px 160px minmax(0, 1fr)",
                      },
                      gap: 2,
                      width: "100%",
                      alignItems: "start",
                    }}
                  >
                    <TextField
                      label="Documento*"
                      name="cpfCnpj"
                      value={form.cpfCnpj}
                      onChange={handleCpfCnpjChange}
                      disabled={isReadOnly}
                      error={campoComErro("cpfCnpj")}
                      helperText={mensagemDocumento || "Informe CPF ou CNPJ"}
                      inputProps={{
                        maxLength: 18,
                        inputMode: "text",
                      }}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="Tipo de Pessoa"
                      name="tipoPessoa"
                      value={form.tipoPessoa}
                      disabled
                      error={campoComErro("tipoPessoa")}
                      helperText={form.tipoPessoa ? `Documento identificado como ${form.tipoPessoa}` : "Identificação automática"}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label={form.tipoPessoa === "PJ" ? "Razão Social*" : form.tipoPessoa === "PF" ? "Nome*" : "Nome/Razão Social*"}
                      name="nomeRazaoSocial"
                      value={form.nomeRazaoSocial}
                      onChange={handleChange}
                      disabled={isReadOnly || consultandoCnpj || nomeBloqueadoReceita}
                      error={campoComErro("nomeRazaoSocial")}
                      helperText={consultandoCnpj ? "Consultando CNPJ..." : nomeBloqueadoReceita ? "Preenchido pela consulta do CNPJ" : ""}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />
                  </Box>

                  {/* =====================================================
    MATRÍCULAS DAS PROPRIEDADES DO IMÓVEL
===================================================== */}
                  <Box
                    sx={{
                      mt: 1,
                      pb: 1,
                      borderBottom: "1px solid var(--ig-b-255-255-255-a018)",
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        color: "var(--ig-t-ffffff)",
                        fontSize: "1.05rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                      }}
                    >
                      Matrículas das Propriedades do Imóvel
                    </h3>
                  </Box>

                  {form.matriculas.map((matricula, index) => (
                    <Box
                      key={matricula.id || `matricula-${index}`}
                      sx={{
                        border: "1px solid var(--ig-b-255-255-255-a018)",
                        borderRadius: "6px",
                        p: 2,
                        backgroundColor: "var(--ig-f-255-255-255-a002)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        <span
                          style={{
                            color: "var(--ig-t-ffffff)",
                            fontWeight: 700,
                          }}
                        >
                          Matrícula {index + 1}
                        </span>

                        {!isReadOnly && (
                          <Button
                            type="button"
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => removerMatricula(index)}
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                            }}
                          >
                            <FaTrash
                              style={{
                                marginRight: 6,
                              }}
                            />
                            Remover
                          </Button>
                        )}
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 0.7fr) minmax(0, 0.7fr) minmax(0, 1.4fr)",
                          gap: 2,
                          width: "100%",
                        }}
                      >
                        <TextField
                          label="Número da Matrícula*"
                          value={matricula.numeroMatricula}
                          onChange={(event) => handleMatriculaChange(index, "numeroMatricula", event.target.value)}
                          disabled={isReadOnly}
                          fullWidth
                          size="small"
                          sx={muiCampoSx}
                        />

                        <TextField
                          label="Data do Documento*"
                          type="date"
                          value={matricula.dataDocumento}
                          onChange={(event) => handleMatriculaChange(index, "dataDocumento", event.target.value)}
                          disabled={isReadOnly}
                          fullWidth
                          size="small"
                          sx={muiCampoSx}
                          slotProps={{
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                        />

                        <TextField
                          label="Livro*"
                          value={matricula.livro}
                          onChange={(event) => handleMatriculaChange(index, "livro", event.target.value)}
                          disabled={isReadOnly}
                          fullWidth
                          size="small"
                          sx={muiCampoSx}
                        />

                        <TextField
                          label="Folha*"
                          value={matricula.folha}
                          onChange={(event) => handleMatriculaChange(index, "folha", event.target.value)}
                          disabled={isReadOnly}
                          fullWidth
                          size="small"
                          sx={muiCampoSx}
                        />

                        <TextField
                          label="Município do Cartório*"
                          value={matricula.municipioCartorio}
                          onChange={(event) => handleMatriculaChange(index, "municipioCartorio", event.target.value)}
                          disabled={isReadOnly}
                          fullWidth
                          size="small"
                          sx={muiCampoSx}
                        />
                      </Box>
                    </Box>
                  ))}

                  {!isReadOnly && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Button
                        type="button"
                        variant="contained"
                        onClick={adicionarMatricula}
                        sx={{
                          height: 44,
                          backgroundColor: "#2196f3",
                          textTransform: "none",
                          fontWeight: 700,

                          "&:hover": {
                            backgroundColor: "#1976d2",
                          },
                        }}
                      >
                        Adicionar Matrícula
                      </Button>
                    </Box>
                  )}

                  {/* CAR ESTADUAL */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "320px",
                      },
                      gap: 2,
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      select
                      label="CAR Estadual?*"
                      name="carEstadual"
                      value={form.carEstadual ? "SIM" : "NÃO"}
                      onChange={(event) => {
                        const marcadoComoSim = event.target.value === "SIM";

                        setForm((anterior) => ({
                          ...anterior,
                          carEstadual: marcadoComoSim,
                        }));

                        if (!marcadoComoSim) {
                          setAbaAtiva("RECIBO");
                        }
                      }}
                      disabled={isReadOnly}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                      SelectProps={{
                        MenuProps: menuPropsDark,
                      }}
                    >
                      <MenuItem value="NÃO">Não</MenuItem>

                      <MenuItem value="SIM">Sim</MenuItem>
                    </TextField>
                  </Box>

                  {/* OBSERVAÇÕES */}
                  <TextField
                    label="Observações"
                    name="observacao"
                    value={form.observacao}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    multiline
                    rows={3}
                    fullWidth
                    size="small"
                    sx={muiCampoSx}
                  />

                  {/* ARQUIVOS GED PENDENTES */}
                  {gedPendentes.length > 0 && (
                    <Box
                      sx={{
                        border: "1px solid rgba(46, 168, 255, 0.45)",
                        borderRadius: "4px",
                        p: 1.5,
                        backgroundColor: "rgba(46, 168, 255, 0.06)",
                      }}
                    >
                      <Box
                        sx={{
                          color: "var(--ig-t-90caf9)",
                          fontWeight: 700,
                          mb: 1,
                        }}
                      >
                        Arquivos selecionados para envio
                      </Box>

                      {gedPendentes.map((arquivo, index) => (
                        <Box
                          key={`${arquivo.name}-${index}`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            py: 0.5,
                            borderBottom: index < gedPendentes.length - 1 ? "1px solid var(--ig-b-255-255-255-a012)" : "none",
                          }}
                        >
                          <span>{arquivo.name}</span>

                          <button type="button" className="btn-excluir-ged" onClick={() => removerArquivoPendente(index)} disabled={salvando || enviandoGED}>
                            <FaTrash />
                            Remover
                          </button>
                        </Box>
                      ))}
                    </Box>
                  )}

                  <input id="uploadGEDCAR" type="file" multiple onChange={selecionarArquivosGED} style={{ display: "none" }} />

                  {/* BOTÕES */}
                  <div className="iptu-botoes">
                    {modoVisualizacao ? (
                      <>
                        {podeEditar && (
                          <button
                            type="button"
                            className="iptu-btn-acao btn-cadastrar"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();

                              const registroAtual = registros.find((item) => Number(item.id) === Number(form.id));

                              if (!registroAtual) {
                                alert("Não foi possível localizar o registro selecionado.");
                                return;
                              }

                              editarRegistro(registroAtual);
                            }}
                          >
                            <FaEdit className="iptu-btn-icone" />
                            Editar
                          </button>
                        )}

                        {podeExcluir && (
                          <button
                            type="button"
                            className="iptu-btn-acao btn-excluir"
                            onClick={() => {
                              const registroAtual = registros.find((item) => Number(item.id) === Number(form.id));

                              if (!registroAtual) {
                                alert("Não foi possível localizar o registro selecionado.");
                                return;
                              }

                              excluirRegistro(registroAtual);
                            }}
                          >
                            <FaTrash className="iptu-btn-icone" />
                            Excluir
                          </button>
                        )}

                        <button type="button" className="iptu-btn-acao btn-acao" onClick={limparFormulario}>
                          <FaTimes className="iptu-btn-icone" />
                          Cancelar
                        </button>

                        <button
                          type="button"
                          className={`iptu-btn-acao btn-ged ${Number(form.qtdArquivos) > 0 ? "btn-ged-ok" : ""}`}
                          onClick={() => abrirGED(form.id, form.codigoCar)}
                        >
                          <FaPaperclip className="iptu-btn-icone" />
                          GED
                          {Number(form.qtdArquivos) > 0 && <span className="ged-badge">{form.qtdArquivos}</span>}
                        </button>
                      </>
                    ) : editandoId ? (
                      <>
                        <button type="submit" className="iptu-btn-acao btn-cadastrar" disabled={salvando || enviandoGED}>
                          <FaEdit className="iptu-btn-icone" />

                          {salvando || enviandoGED ? "Salvando..." : "Atualizar"}
                        </button>

                        <button type="button" className="iptu-btn-acao btn-acao" onClick={limparFormulario} disabled={salvando || enviandoGED}>
                          <FaTimes className="iptu-btn-icone" />
                          Cancelar
                        </button>

                        <button
                          type="button"
                          className={`iptu-btn-acao btn-ged ${Number(form.qtdArquivos) > 0 || gedPendentes.length > 0 ? "btn-ged-ok" : ""}`}
                          onClick={() => {
                            document.getElementById("uploadGEDCAR")?.click();
                          }}
                          disabled={salvando || enviandoGED}
                        >
                          <FaPaperclip className="iptu-btn-icone" />
                          GED
                          {gedPendentes.length > 0 && <span className="ged-badge">{gedPendentes.length}</span>}
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="submit" className="iptu-btn-acao btn-cadastrar" disabled={salvando || enviandoGED}>
                          <FaSave className="iptu-btn-icone" />

                          {salvando || enviandoGED ? "Salvando..." : "Cadastrar"}
                        </button>

                        <button type="button" className="iptu-btn-acao btn-acao" onClick={limparFormulario} disabled={salvando || enviandoGED}>
                          <FaTimes className="iptu-btn-icone" />
                          Cancelar
                        </button>

                        <button
                          type="button"
                          className={`iptu-btn-acao btn-ged ${gedPendentes.length > 0 ? "btn-ged-ok" : ""}`}
                          onClick={() => {
                            document.getElementById("uploadGEDCAR")?.click();
                          }}
                          disabled={salvando || enviandoGED}
                        >
                          <FaPaperclip className="iptu-btn-icone" />
                          GED
                          {gedPendentes.length > 0 && <span className="ged-badge">{gedPendentes.length}</span>}
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}

              {abaAtiva === "ESTADUAL" && Boolean(form.carEstadual) && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    py: 2,
                  }}
                >
                  {carregandoCAREstadual ? (
                    <Box
                      sx={{
                        color: "var(--ig-t-90caf9)",
                        py: 3,
                      }}
                    >
                      Carregando dados do CAR Estadual...
                    </Box>
                  ) : (
                    <>
                      {/* DADOS DO CERTIFICADO */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "minmax(0, 1fr) minmax(0, 1fr)",
                          },
                          gap: 2,
                        }}
                      >
                        <TextField
                          label="Certificado de Inscrição Número*"
                          value={formCAREstadual.certificadoInscricaoNumero}
                          disabled
                          fullWidth
                          size="small"
                          sx={muiCampoSx}
                        />

                        <TextField
                          label="Código de Segurança*"
                          name="codigoSeguranca"
                          value={formCAREstadual.codigoSeguranca}
                          onChange={handleCAREstadualChange}
                          disabled={isReadOnly || salvandoCAREstadual}
                          fullWidth
                          size="small"
                          sx={muiCampoSx}
                        />
                      </Box>

                      {/* ÁREAS */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "minmax(0, 1fr) minmax(0, 1fr)",
                          },
                          gap: 2,
                        }}
                      >
                        <TextField
                          label="Área Total Documentada (ha)*"
                          name="areaTotalDocumentada"
                          value={formCAREstadual.areaTotalDocumentada}
                          onChange={(event) => {
                            const valor = mascaraDecimalDigitacaoBR(event.target.value);

                            setFormCAREstadual((anterior) => ({
                              ...anterior,
                              areaTotalDocumentada: valor,
                            }));
                          }}
                          onBlur={() => {
                            setFormCAREstadual((anterior) => ({
                              ...anterior,

                              areaTotalDocumentada: anterior.areaTotalDocumentada ? mascaraDecimal4BR(anterior.areaTotalDocumentada) : "",
                            }));
                          }}
                          disabled={isReadOnly || salvandoCAREstadual}
                          fullWidth
                          size="small"
                          sx={muiCampoSx}
                        />

                        <TextField
                          label="Área Total Calculada (ha)*"
                          name="areaTotalCalculada"
                          value={formCAREstadual.areaTotalCalculada}
                          disabled
                          fullWidth
                          size="small"
                          sx={muiCampoSx}
                        />
                      </Box>

                      {/* =====================================================
    RESPONSÁVEIS PELO DOCUMENTO
===================================================== */}
                      <Box
                        sx={{
                          mt: 1,
                          pb: 1,
                          borderBottom: "1px solid var(--ig-b-255-255-255-a018)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                        }}
                      >
                        <h3
                          style={{
                            margin: 0,
                            color: "var(--ig-t-ffffff)",
                            fontSize: "1.05rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                          }}
                        >
                          Responsáveis pelo Documento
                        </h3>

                        {!isReadOnly && (
                          <Button
                            type="button"
                            variant="contained"
                            onClick={adicionarResponsavelCAREstadual}
                            disabled={salvandoCAREstadual}
                            sx={{
                              minWidth: "190px",
                              height: "36px",
                              color: "#ffffff",
                              backgroundColor: "#1976d2",
                              fontWeight: 700,
                              fontSize: "12px",
                              textTransform: "none",

                              "&:hover": {
                                backgroundColor: "#1565c0",
                              },
                            }}
                          >
                            + Adicionar responsável
                          </Button>
                        )}
                      </Box>

                      {(Array.isArray(formCAREstadual.responsaveis) && formCAREstadual.responsaveis.length > 0
                        ? formCAREstadual.responsaveis
                        : [criarResponsavelCAREstadualVazio()]
                      ).map((responsavel, indice) => {
                        const possuiBotaoRemover = (formCAREstadual.responsaveis?.length || 0) > 1 && !isReadOnly;

                        return (
                          <Box
                            key={`responsavel-${indice}`}
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                md: possuiBotaoRemover ? "260px 160px minmax(0, 1fr) 44px" : "260px 160px minmax(0, 1fr)",
                              },
                              gap: 2,
                              width: "100%",
                              alignItems: "start",
                            }}
                          >
                            <TextField
                              label="Documento*"
                              value={responsavel.documento}
                              onChange={(event) => alterarDocumentoResponsavelCAREstadual(indice, event.target.value)}
                              disabled={isReadOnly || salvandoCAREstadual}
                              helperText="Informe CPF ou CNPJ"
                              inputProps={{
                                maxLength: 18,
                                inputMode: "text",
                              }}
                              fullWidth
                              size="small"
                              sx={muiCampoSx}
                            />

                            <TextField
                              label="Tipo de Pessoa"
                              value={responsavel.tipoPessoa}
                              disabled
                              helperText={responsavel.tipoPessoa ? `Documento identificado como ${responsavel.tipoPessoa}` : "Identificação automática"}
                              fullWidth
                              size="small"
                              sx={muiCampoSx}
                            />

                            <TextField
                              label={responsavel.tipoPessoa === "PJ" ? "Razão Social*" : responsavel.tipoPessoa === "PF" ? "Nome*" : "Nome/Razão Social*"}
                              value={responsavel.nome}
                              onChange={(event) => alterarNomeResponsavelCAREstadual(indice, event.target.value)}
                              disabled={isReadOnly || salvandoCAREstadual}
                              helperText=""
                              fullWidth
                              size="small"
                              sx={muiCampoSx}
                            />

                            {possuiBotaoRemover && (
                              <Tooltip title="Remover responsável">
                                <span>
                                  <IconButton
                                    type="button"
                                    onClick={() => removerResponsavelCAREstadual(indice)}
                                    disabled={salvando}
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      color: "#ef4444",
                                      border: "1px solid rgba(239, 68, 68, 0.65)",
                                      borderRadius: "4px",

                                      "&:hover": {
                                        color: "#ffffff",
                                        backgroundColor: "#dc2626",
                                      },
                                    }}
                                  >
                                    <FaTrash size={15} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        );
                      })}

                      {/* =====================================================
    CADASTRANTE
===================================================== */}
                      <Box
                        sx={{
                          mt: 1,
                          pb: 1,
                          borderBottom: "1px solid var(--ig-b-255-255-255-a018)",
                        }}
                      >
                        <h3
                          style={{
                            margin: 0,
                            color: "var(--ig-t-ffffff)",
                            fontSize: "1.05rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                          }}
                        >
                          Cadastrante
                        </h3>
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "260px 160px minmax(0, 1fr)",
                          },
                          gap: 2,
                          width: "100%",
                          alignItems: "start",
                        }}
                      >
                        <TextField
                          label="Documento*"
                          name="cadastranteDocumento"
                          value={formCAREstadual.cadastranteDocumento}
                          onChange={handleDocumentoCAREstadual("cadastranteDocumento", "cadastranteTipoPessoa", "cadastranteNome")}
                          disabled={isReadOnly || salvandoCAREstadual}
                          helperText="Informe CPF ou CNPJ"
                          inputProps={{
                            maxLength: 18,
                            inputMode: "text",
                          }}
                          fullWidth
                          size="small"
                          sx={muiCampoSx}
                        />

                        <TextField
                          label="Tipo de Pessoa"
                          value={formCAREstadual.cadastranteTipoPessoa}
                          disabled
                          helperText={
                            formCAREstadual.cadastranteTipoPessoa
                              ? `Documento identificado como ${formCAREstadual.cadastranteTipoPessoa}`
                              : "Identificação automática"
                          }
                          fullWidth
                          size="small"
                          sx={muiCampoSx}
                        />

                        <TextField
                          label={
                            formCAREstadual.cadastranteTipoPessoa === "PJ"
                              ? "Razão Social*"
                              : formCAREstadual.cadastranteTipoPessoa === "PF"
                                ? "Nome*"
                                : "Nome/Razão Social*"
                          }
                          name="cadastranteNome"
                          value={formCAREstadual.cadastranteNome}
                          onChange={handleCAREstadualChange}
                          disabled={isReadOnly || salvandoCAREstadual}
                          helperText=""
                          fullWidth
                          size="small"
                          sx={muiCampoSx}
                        />
                      </Box>

                      {/* BOTÕES DO CAR ESTADUAL */}
                      <div
                        className="iptu-botoes"
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "10px",
                          width: "100%",
                        }}
                      >
                        {!isReadOnly && (
                          <button type="submit" className="iptu-btn-acao btn-cadastrar" disabled={salvando || salvandoCAREstadual || enviandoGED}>
                            {!salvando && !salvandoCAREstadual && !enviandoGED && !editandoId && <FaSave className="iptu-btn-icone" />}

                            {salvando || salvandoCAREstadual || enviandoGED ? "Salvando..." : editandoId ? "Atualizar" : "Cadastrar"}
                          </button>
                        )}

                        <button
                          type="button"
                          className="iptu-btn-acao btn-acao"
                          onClick={cancelarCAREstadual}
                          disabled={salvando || salvandoCAREstadual || enviandoGED}
                          style={{
                            flex: "0 0 auto",
                            width: "140px",
                            minWidth: "140px",
                          }}
                        >
                          <FaTimes className="iptu-btn-icone" />
                          Cancelar
                        </button>
                      </div>
                    </>
                  )}
                </Box>
              )}

              {abaAtiva === "VERSIONAMENTO" && (
                <Box
                  sx={{
                    gridColumn: "1 / -1",
                    width: "100%",
                    mt: 2,
                  }}
                >
                  {carregandoVersoes ? (
                    <Box
                      sx={{
                        py: 4,
                        textAlign: "center",
                        color: "var(--ig-t-d1d5db)",
                      }}
                    >
                      Carregando versionamento...
                    </Box>
                  ) : versoesCAR.length === 0 ? (
                    <Box
                      sx={{
                        py: 4,
                        textAlign: "center",
                        color: "var(--ig-t-d1d5db)",
                        border: "1px solid var(--ig-b-334155)",
                        borderRadius: 1,
                      }}
                    >
                      Nenhuma versão anterior registrada.
                    </Box>
                  ) : (
                    <Table
                      size="small"
                      sx={{
                        "& .MuiTableCell-root": {
                          color: "var(--ig-t-ffffff)",
                          borderColor: "var(--ig-b-334155)",
                        },

                        "& .MuiTableHead-root .MuiTableCell-root": {
                          fontWeight: 700,
                          backgroundColor: "var(--ig-f-182334)",
                        },

                        "& .MuiTableBody-root .MuiTableRow-root:hover": {
                          backgroundColor: "var(--ig-f-255-255-255-a005)",
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>Versão</TableCell>
                          <TableCell>Justificativa</TableCell>
                          <TableCell>Alterado por</TableCell>
                          <TableCell>Data e hora</TableCell>
                          <TableCell align="center">Ações</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {versoesCAR.map((versao) => (
                          <TableRow key={versao.id}>
                            <TableCell>{versao.numeroVersao}</TableCell>

                            <TableCell>{versao.justificativa || "-"}</TableCell>

                            <TableCell>{versao.alteradoPorNome || "-"}</TableCell>

                            <TableCell>{formatarDataHoraBR(versao.alteradoEm)}</TableCell>

                            <TableCell align="center">
                              <Button
                                type="button"
                                variant="contained"
                                size="small"
                                onClick={() => visualizarVersaoCAR(versao.id)}
                                sx={{
                                  minWidth: 110,
                                  textTransform: "none",
                                  fontWeight: 700,
                                }}
                              >
                                <FaEye
                                  style={{
                                    marginRight: 6,
                                  }}
                                />
                                Visualizar
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              )}
            </form>
          </div>
        ) : (
          <div className="aviso-permissao-container">
            <p className="aviso-permissao">🔒 Você possui acesso apenas de visualização.</p>
          </div>
        )}

        <Dialog
          open={modalJustificativaAberto}
          onClose={fecharModalJustificativa}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: "var(--ig-f-2c2c2c)",
              color: "var(--ig-t-ffffff)",
              backgroundImage: "none",
            },
          }}
        >
          <DialogTitle
            sx={{
              color: "var(--ig-t-ffffff)",
              fontWeight: 700,
            }}
          >
            Justificativa da alteração
          </DialogTitle>

          <DialogContent>
            <Box
              sx={{
                pt: 1,
              }}
            >
              <TextField
                label="Motivo da alteração*"
                value={justificativaEdicao}
                onChange={(event) => setJustificativaEdicao(event.target.value.slice(0, 1000))}
                multiline
                minRows={4}
                fullWidth
                autoFocus
                helperText={`${justificativaEdicao.length}/1000 caracteres`}
                sx={muiCampoSx}
              />
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              pb: 2,
              gap: 1,
            }}
          >
            <Button
              type="button"
              onClick={fecharModalJustificativa}
              variant="outlined"
              sx={{
                color: "var(--ig-t-ffffff)",
                borderColor: "#64748b",
                textTransform: "none",
              }}
            >
              Cancelar
            </Button>

            <Button
              type="button"
              onClick={confirmarEdicao}
              variant="contained"
              disabled={!justificativaEdicao.trim()}
              sx={{
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Continuar edição
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={modalVersaoAberto}
          onClose={fecharModalVersao}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: "var(--ig-f-2c2c2c)",
              color: "var(--ig-t-ffffff)",
              backgroundImage: "none",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              color: "var(--ig-t-ffffff)",
              fontWeight: 700,
            }}
          >
            <span>{versaoSelecionada ? `Versão anterior nº ${versaoSelecionada.numeroVersao}` : "Versão anterior do CAR"}</span>

            <IconButton type="button" onClick={fecharModalVersao}>
              <CloseIcon sx={{ color: "var(--ig-t-aaaaaa)" }} />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            {carregandoVersao ? (
              <Box
                sx={{
                  py: 5,
                  textAlign: "center",
                  color: "var(--ig-t-d1d5db)",
                }}
              >
                Carregando versão...
              </Box>
            ) : versaoSelecionada ? (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "1fr 1fr",
                    },
                    gap: 2,
                    mb: 3,
                    p: 2,
                    border: "1px solid var(--ig-b-475569)",
                    borderRadius: 1,
                  }}
                >
                  <Box>
                    <strong>Alterado por:</strong> {versaoSelecionada.alteradoPorNome || "-"}
                  </Box>

                  <Box>
                    <strong>Data e hora:</strong> {formatarDataHoraBR(versaoSelecionada.alteradoEm)}
                  </Box>

                  <Box
                    sx={{
                      gridColumn: "1 / -1",
                    }}
                  >
                    <strong>Justificativa:</strong> {versaoSelecionada.justificativa || "-"}
                  </Box>
                  <Box
                    sx={{
                      gridColumn: "1 / -1",
                    }}
                  >
                    <strong>Campos alterados:</strong>{" "}
                    {Array.isArray(versaoSelecionada.camposAlterados) && versaoSelecionada.camposAlterados.length > 0
                      ? versaoSelecionada.camposAlterados.map((campo) => obterNomeCampoVersao(normalizarCampoAlterado(campo))).join(", ")
                      : "-"}
                  </Box>
                </Box>

                <Box
                  component="h3"
                  sx={{
                    mt: 0,
                    color: "var(--ig-t-ffffff)",
                  }}
                >
                  Comparação da versão
                </Box>

                <Table
                  size="small"
                  sx={{
                    "& .MuiTableCell-root": {
                      color: "var(--ig-t-ffffff)",
                      borderColor: "var(--ig-b-475569)",
                    },

                    "& .MuiTableHead-root .MuiTableCell-root": {
                      fontWeight: 700,
                      backgroundColor: "var(--ig-f-182334)",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Campo</TableCell>

                      <TableCell>{`Versão anterior n° ${versaoSelecionada?.numeroVersao || ""}`}</TableCell>

                      <TableCell>{`Versão atual n° ${versaoSelecionada?.numeroVersao ? Number(versaoSelecionada.numeroVersao) + 1 : ""}`}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {obterLinhasComparacaoVersao(versaoSelecionada).map(({ campo, nomeCampo, valorAnterior, valorNovo, alterado }) => (
                      <TableRow
                        key={campo}
                        sx={{
                          backgroundColor: alterado ? "rgba(245, 158, 11, 0.18)" : "transparent",

                          "&:hover": {
                            backgroundColor: alterado ? "rgba(245, 158, 11, 0.26)" : "var(--ig-f-255-255-255-a004)",
                          },

                          "& .MuiTableCell-root": {
                            fontWeight: alterado ? 700 : 400,
                          },

                          "& .MuiTableCell-root:first-of-type": {
                            borderLeft: alterado ? "4px solid #f59e0b" : "4px solid transparent",
                          },
                        }}
                      >
                        <TableCell>
                          {nomeCampo}

                          {alterado && (
                            <Box
                              component="span"
                              sx={{
                                ml: 1,
                                px: 1,
                                py: 0.25,
                                borderRadius: "10px",
                                backgroundColor: "#f59e0b",
                                color: "#111827",
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                              }}
                            >
                              ALTERADO
                            </Box>
                          )}
                        </TableCell>

                        <TableCell>{formatarValorVersao(campo, valorAnterior)}</TableCell>

                        <TableCell>{formatarValorVersao(campo, valorNovo)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            ) : (
              <Box
                sx={{
                  py: 4,
                  textAlign: "center",
                }}
              >
                Versão não encontrada.
              </Box>
            )}
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              pb: 2,
            }}
          >
            <Button
              type="button"
              onClick={fecharModalVersao}
              variant="contained"
              sx={{
                textTransform: "none",
              }}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        {/* MODAL SELECIONAR IMÓVEL - PADRÃO IPTU */}
        <Dialog
          open={modalImovelAberto}
          onClose={fecharModalImovel}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: "var(--ig-f-2c2c2c)",
              color: "var(--ig-t-ffffff)",
              backgroundImage: "none",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              color: "var(--ig-t-ffffff)",
              fontWeight: 700,
            }}
          >
            Selecionar Imóvel
            <IconButton type="button" onClick={fecharModalImovel}>
              <CloseIcon sx={{ color: "var(--ig-t-aaaaaa)" }} />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            {/* CAMPO DE PESQUISA AUTOMÁTICA */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                mb: 2,
              }}
            >
              <TextField
                value={buscaImovel}
                onChange={(event) => {
                  setBuscaImovel(event.target.value);
                  setPaginaImoveis(0);
                }}
                placeholder="Digite para pesquisar por código, apelido, endereço, bairro ou cidade."
                autoFocus
                fullWidth
                size="small"
                sx={muiCampoSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* TABELA DE IMÓVEIS */}
            {carregandoImoveis ? (
              <Box
                sx={{
                  py: 4,
                  textAlign: "center",
                  color: "var(--ig-t-d1d5db)",
                }}
              >
                Carregando imóveis...
              </Box>
            ) : imoveis.length === 0 ? (
              <Box
                sx={{
                  py: 4,
                  textAlign: "center",
                  color: "var(--ig-t-d1d5db)",
                }}
              >
                Nenhum imóvel encontrado.
              </Box>
            ) : (
              <Table
                size="small"
                sx={{
                  "& .MuiTableCell-root": {
                    color: "var(--ig-t-ffffff)",
                    borderBottom: "1px solid var(--ig-b-255-255-255-a014)",
                  },

                  "& .MuiTableHead-root .MuiTableCell-root": {
                    backgroundColor: "var(--ig-f-1f1f1f)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  },

                  "& .MuiTableBody-root .MuiTableRow-root:hover": {
                    backgroundColor: "var(--ig-f-255-255-255-a008)",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Apelido</TableCell>
                    <TableCell>Endereço</TableCell>
                    <TableCell>Bairro</TableCell>
                    <TableCell>Cidade/UF</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {imoveis.map((imovel) => (
                    <TableRow
                      key={imovel.id || imovel.codigoMatricula || imovel.apelido}
                      hover
                      onClick={() => selecionarImovel(imovel)}
                      sx={{
                        cursor: "pointer",
                      }}
                    >
                      <TableCell>{imovel.codigoMatricula || ""}</TableCell>

                      <TableCell
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        {imovel.apelido || ""}
                      </TableCell>

                      <TableCell>{imovel.endereco || ""}</TableCell>

                      <TableCell>{imovel.bairro || ""}</TableCell>

                      <TableCell>{[imovel.cidade, imovel.uf].filter(Boolean).join("/")}</TableCell>

                      <TableCell>{imovel.status || ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {/* PAGINAÇÃO */}
            <TablePagination
              component="div"
              count={totalImoveis}
              page={paginaImoveis}
              onPageChange={(_, novaPagina) => {
                setPaginaImoveis(novaPagina);

                carregarImoveis(buscaImovel, novaPagina, imoveisPorPagina);
              }}
              rowsPerPage={imoveisPorPagina}
              onRowsPerPageChange={(event) => {
                const quantidade = parseInt(event.target.value, 10);

                setImoveisPorPagina(quantidade);
                setPaginaImoveis(0);

                carregarImoveis(buscaImovel, 0, quantidade);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: "var(--ig-f-2c2c2c)",
                      color: "var(--ig-t-ffffff)",

                      "& .MuiMenuItem-root": {
                        color: "var(--ig-t-ffffff)",
                      },

                      "& .MuiMenuItem-root:hover": {
                        backgroundColor: "var(--ig-f-255-255-255-a008)",
                      },

                      "& .MuiMenuItem-root.Mui-selected": {
                        backgroundColor: "var(--ig-f-255-255-255-a012)",
                      },
                    },
                  },
                },
              }}
              sx={{
                color: "var(--ig-t-ffffff)",

                "& .MuiTablePagination-selectIcon": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiTablePagination-actions button": {
                  color: "var(--ig-t-ffffff)",
                },
              }}
            />
          </DialogContent>
        </Dialog>
      </div>
    </ThemeProvider>
  );
}

export default CAR;
